// src/lib/types.ts
//
// Response shapes for the gateway's REST surface (`services/gateway/src/routes/*.ts`).
// Read from the actual route handlers, not guessed. Where a field is a branded
// type on the server (`Cents`, `ServiceDate`, `Instant`, `BasisPoints`), it
// crosses JSON as the plain primitive the brand wraps — a `Cents` becomes a
// `number`, a `ServiceDate` becomes a `string` — so these are typed as plain
// primitives here rather than importing the runtime brands, which would invite
// constructing one from unvalidated client input.
//
// `Role`, `Scope`, `Grant`, `BillType` ARE imported from `@prs2/auth`: those are
// plain structural types with no server-only runtime behind them, and importing
// them keeps the role/scope vocabulary in one place instead of a second,
// driftable copy here.
import type { BillType, Role, Scope } from '@prs2/auth'
import type {
  BillStatusValue,
  DemonstrationModeReport,
  IdentityProviderReport,
  NotificationDeliveryReport,
  SettlementDispatchReport,
} from '@prs2/contracts'
// ⚠ A VALUE import, not a type one — `BILL_STATUSES` is iterated to build the
// status `<select>` on two screens, so the array itself has to exist at runtime.
// It is a frozen tuple of string literals with no server-only runtime behind it,
// which is the same test `Role`/`Scope` above pass.
import { BILL_STATUS_VALUES } from '@prs2/contracts'

export type { BillType, Role, Scope }

// ─────────────────────────────────────────────────────────────────────────
// ★★ WAVE 8 (Task 3) — THE BOUNDS THE SERVER PUBLISHES, as every
// administration catalogue's list route now returns them.
// ─────────────────────────────────────────────────────────────────────────

/**
 * The validation bounds for ONE write-form field, exactly as
 * `services/gateway/src/routes/administration-field-bounds.ts` declares them —
 * the same object the route builds its Fastify schema and its refusals from.
 *
 * ⚠ **Every property is optional, and an absent one means the server enforces
 * nothing on that axis.** A screen must therefore show nothing on that axis
 * either: a default here (or a `?? 200` at a call site) would manufacture the
 * bound this whole mechanism exists to prevent. `ReferenceDataPage` has consumed
 * the equivalent block since Wave 3 (`ReferenceParameterCatalogueEntry.validation`);
 * this is the same idea for the eleven catalogues that had none.
 */
export interface PublishedFieldBounds {
  readonly minLength?: number
  readonly maxLength?: number
  /** An anchored regular-expression SOURCE — the route's own, never one written here. */
  readonly pattern?: string
  readonly min?: number
}

/**
 * One catalogue's published bounds, keyed by the request field each governs.
 *
 * ⚠ The key union is written out per catalogue rather than left as `string`, so a
 * field renamed on the server fails `pnpm typecheck` here instead of silently
 * publishing `undefined` to a screen that would then show no bound at all —
 * which reads exactly like a field that legitimately has none.
 */
export type PublishedBounds<Field extends string> = Readonly<Record<Field, PublishedFieldBounds>>

/** `GET /api/identities` — the federated-identity stub's roster, for the login screen. */
export interface DemoIdentity {
  readonly email: string
  readonly displayName: string
  readonly role: Role
  /**
   * ★★ WAVE 8 — the option label, **composed by the SERVER**:
   * `Name — Role · agencies · bill types · approval authority`, plus a leading
   * `MACHINE IDENTITY ·` for an unattended caller.
   *
   * ⚠ **This screen renders it verbatim and derives NOTHING from raw grants.** A
   * summary assembled here from `approval_level`/`approval_limit` would be a
   * second implementation of an authorisation rule this app does not own — S-011,
   * the defect `published-bounds.test.ts` is the census for — and it would drift
   * silently in the direction that makes the screen look right. The composition,
   * and the reason every authority claim in it is PROBED through `authorise()`
   * rather than described, live in `services/gateway/src/identity-roster.ts`.
   *
   * ⚠ **The ceiling in it is MONEY, not cents.** `app.user_scope.approval_limit`
   * is an integer number of cents (`300000` is SGD 3,000.00); the server formats
   * it with `formatCentsExact` before it ever reaches this type.
   *
   * ⚠ Declared REQUIRED because it is part of this route's contract now, and a
   * fixture that omitted it would stop resembling the response. It still arrives
   * over a `JSON.parse` cast, so `LoginPage` names what it falls back to when a
   * gateway older than this bundle sends none — see `optionLabel` there.
   */
  readonly label: string
}

/**
 * `GET /api/identities` response, **as `lib/api.ts` hands it out** — not the raw
 * wire shape.
 *
 * ⚠ `environment`, `identityProvider` and `settlementDispatch` are the three
 * fields this app reads to describe the GATEWAY rather than the data, and all
 * three are narrowed at the API boundary (`requestSelfReporting`). They are
 * therefore the only fields on these three responses that are not a bare cast,
 * which is why they are the only ones whose absence is survivable. See that
 * function for the fail-open this closes.
 *
 * `environment` is the gateway's own `APP_ENV` (see `GatewayDeps.environment`
 * server-side) — read from here rather than from `import.meta.env` so the badge
 * reflects where this build actually deployed to, not what was baked into the
 * bundle when it was built. `null` when the response carried no string for it.
 */
export interface IdentitiesResponse {
  readonly identities: readonly DemoIdentity[]
  /**
   * ★★ WAVE 6 — where to send the browser when the gateway has a FEDERATED
   * identity provider installed (WOG ADFS / GCC Entra ID over OIDC,
   * P2C3 §1.1.1(e)).
   *
   * ⚠ **Present exactly when `identities` is empty, and absent otherwise** —
   * `GET /api/identities` returns one shape or the other from a single branch on
   * which provider `buildServer` composed, never both. So this field is the
   * client's whole test for "is this deployment federated": there is no second
   * flag to keep in step, and a client that hard-coded the answer would be
   * asserting a fact about the server it cannot see.
   *
   * ⚠ It carries `state`, `nonce` and a PKCE `code_challenge` minted SERVER-SIDE
   * and held there. Nothing about the challenge is reconstructible here, which is
   * why this is an opaque URL to navigate to rather than parameters to assemble.
   */
  readonly authorizationUrl?: string
  readonly environment: string | null
  /**
   * What this response said about which identity provider the gateway installed
   * (`packages/contracts/src/identity-provider.ts`).
   *
   * ⚠ NOT `IdentityProvider`, and no longer a bare `string` either. The branded
   * union would be a CLAIM about the server rather than a check on it — the
   * exact mistake `AuditEvent.outcome` records below — and `string` was the same
   * mistake one step quieter: it said a value would always be there, over a
   * `JSON.parse` that cannot promise that. A response omitting the field arrived
   * as `undefined`, which `lib/environment.ts` read as "not reported yet" and
   * rendered no badge and no warning for, on a build that may be signing people
   * in with no credential at all.
   *
   * `IdentityProviderReport` is built from the response by `readIdentityProvider`
   * at the boundary, so it carries what the server SAID — including that it said
   * nothing — and cannot be confused with the pre-response window, which is
   * represented by having no report at all.
   *
   * ⚠ This is what the synthetic-data warning turns on. `environment` above is
   * a DEPLOYMENT NAME and, per its own contract, carries no information about
   * what is authenticating anyone.
   */
  readonly identityProvider: IdentityProviderReport
  /**
   * What this response said about whether anything done on this gateway can
   * reach a Payment Agency (`packages/contracts/src/settlement-dispatch.ts`).
   *
   * ⚠ **This licenses the notice's SECOND claim, and only that one.** The notice
   * used to assert *"no real Authority data is used, **and** nothing you do here
   * reaches a live payment agency"* as one flat fact. The server can vouch for
   * the payment half exactly — it knows what it wired — and `'none'`/`'simulated'`
   * narrowed through `isSettlementDispatch` is the only licence to print it.
   *
   * ⚠ It says NOTHING about the data half, deliberately. See the contract: a
   * synthetic-seed marker would survive real data being loaded beside it, which
   * is S-016's own defect class one layer down rather than a fix for it. No field
   * here is evidence for "no real Authority data is used", so the client no
   * longer states it.
   */
  readonly settlementDispatch: SettlementDispatchReport
  /**
   * ★ TASK 5a. What this response said about whether this gateway is a live
   * DEMONSTRATION of the production build, for an Authority audience
   * (`packages/contracts/src/demonstration-mode.ts`).
   *
   * ⚠ Read from the SERVER, never from this build's own bundle — the same
   * argument `environment` and `identityProvider` already make: a bundle is
   * copied between environments far more casually than a deployment's
   * configuration is. `'on'`, narrowed through `isDemonstrationMode`, is the
   * only licence for the Shell's demonstration banner; anything else —
   * `'off'`, unrecognised, or a response that said nothing — must not show it.
   */
  readonly demonstrationMode: DemonstrationModeReport
  /**
   * ★★ THE FINAL INTEGRATION REVIEW of the 2026-08-15 notification-delivery
   * wave. What this response said about whether anything queued here can leave
   * the building as an email (`packages/contracts/src/notification-delivery.ts`).
   *
   * ⚠ The gateway has reported this field on all three identity/session
   * responses since Task 5 of that wave, and until now NOTHING in this app read
   * it here — `lib/environment.ts` still took three arguments. That is the same
   * omission this file's `identityProvider` note records, one capability over,
   * and what it admitted was a passwordless roster rendering no alarm beside a
   * live mail relay. It licenses the `DEMO IDENTITIES WITH LIVE MAIL` danger
   * branch and nothing else — see `environmentBanner`.
   */
  readonly notificationDelivery: NotificationDeliveryReport
}

/** `POST /api/session` response. */
export interface SessionResponse {
  /**
   * ★ ADR 0015 — the person behind this session is a System Administrator and the gateway offers acting as another account. Absent
   * (read as false) from a gateway without the feature. It does NOT say whether they are acting as somebody now: by decision that
   * is not announced.
   */
  readonly canSwitchUser?: boolean
  readonly session: {
    readonly id: string
    readonly email: string
    readonly displayName: string
    readonly issuedAt: string
  }
  readonly scope: Scope
  /** Same server-reported value as `IdentitiesResponse.environment` — see there. */
  readonly environment: string | null
  /** Same server-reported value as `IdentitiesResponse.identityProvider` — see there. */
  readonly identityProvider: IdentityProviderReport
  /** Same server-reported value as `IdentitiesResponse.settlementDispatch` — see there. */
  readonly settlementDispatch: SettlementDispatchReport
  /** Same server-reported value as `IdentitiesResponse.demonstrationMode` — see there. */
  readonly demonstrationMode: DemonstrationModeReport
  /** Same server-reported value as `IdentitiesResponse.notificationDelivery` — see there. */
  readonly notificationDelivery: NotificationDeliveryReport
}

/** `GET /api/me` response — flatter than `SessionResponse`: no nested `session`. */
export interface MeResponse {
  readonly canSwitchUser?: boolean
  /** ⚠ The SESSION id, not the user id. Compare people by `userId` when present, never by this. */
  readonly id: string
  /** The signed-in user's own id, when the gateway reports it. Absent on a gateway that only reports `id`. */
  readonly userId?: string
  readonly email: string
  readonly displayName: string
  readonly scope: Scope
  readonly issuedAt: string
  /** Same server-reported value as `IdentitiesResponse.environment` — see there. */
  readonly environment: string | null
  /** Same server-reported value as `IdentitiesResponse.identityProvider` — see there. */
  readonly identityProvider: IdentityProviderReport
  /** Same server-reported value as `IdentitiesResponse.settlementDispatch` — see there. */
  readonly settlementDispatch: SettlementDispatchReport
  /** Same server-reported value as `IdentitiesResponse.demonstrationMode` — see there. */
  readonly demonstrationMode: DemonstrationModeReport
  /** Same server-reported value as `IdentitiesResponse.notificationDelivery` — see there. */
  readonly notificationDelivery: NotificationDeliveryReport
}

/** The shape the app actually holds in memory, unified from either response above. */
export interface CurrentUser {
  /** See {@link SessionResponse.canSwitchUser}. */
  readonly canSwitchUser?: boolean
  /** The SESSION id (what `GET /api/me` calls `id`). */
  readonly id: string
  /** The user's own id, when the gateway reports one. */
  readonly userId?: string
  readonly email: string
  readonly displayName: string
  readonly scope: Scope
  readonly issuedAt: string
}

/**
 * ★★ WAVE 10 — `INFORMATION_REQUESTED` added (migration 060, Annex A 37/38).
 *
 * ⚠ **A SIXTH MEMBER IS A CHANGE WITH A BLAST RADIUS, AND THE GUARDS KNOW IT.**
 * `status-breakdown.test.ts` fails unless exactly one dashboard tile claims every
 * member of this union AND the union agrees with the SQL domain — both
 * declarations of it, now that 060 redeclares 003's CHECK. That suite's own header
 * predicted this exact addition: *"a SIXTH status added by a migration would land
 * in `total` and in no tile"*. It landed in a tile.
 *
 * ★★★ **WAVE 11 — THE MEMBERS MOVED TO `@prs2/contracts` AND THIS IS NOW AN
 * ALIAS.** They had to: Wave 11's `?status=` filter has to REFUSE a value outside
 * the domain, so `services/billing` needs the same list, and a second hand-written
 * copy of a status domain drifts SILENTLY IN BOTH DIRECTIONS — a stale server list
 * 400s a status the screen can display, a stale browser list leaves a real status
 * untileable. `packages/contracts/src/bill-status.ts` carries the argument.
 *
 * ⚠ The NAME stays `BILL_STATUSES` here rather than the imported spelling, because
 * `status-breakdown.test.ts` compares that identifier to the SQL and every page
 * imports it — renaming it would have made this a fifteen-file change for nothing.
 * The single declaration is what mattered, not the identifier.
 */
export const BILL_STATUSES = BILL_STATUS_VALUES
export type BillStatus = BillStatusValue

/**
 * Pro-ration provenance: which rule id and version, at what rate, was applied.
 * `null` for a bill nothing has computed yet — see `BillRow` in
 * `packages/persistence/src/bill-repository.ts`.
 */
export interface AppliedRule {
  readonly id: string
  readonly version: number
  /** Basis points: 1/100 of a percent. 6000 = 60%. */
  readonly rate: number
}

/** `GET /api/bills` and `GET /api/bills/:reference` — one row of `app.bill`. */
export interface Bill {
  readonly reference: string
  readonly officerId: string
  readonly agencyCode: string
  readonly billType: BillType
  /** Integer cents. Format for display only — never arithmetic in this app. */
  readonly amount: number
  /** ISO calendar date, YYYY-MM-DD. Entitlement is resolved against THIS date. */
  readonly serviceDate: string
  /** ISO calendar date, YYYY-MM-DD. Never used for entitlement resolution. */
  readonly submissionDate: string
  readonly status: BillStatus | string
  readonly subsidy: number | null
  readonly coPayment: number | null
  readonly appliedRule: AppliedRule | null
  /** Canonical UTC instant, or null if not yet computed. */
  readonly computedAt: string | null
}

/**
 * ★ TASK 5D. `total`/`nextCursor` are new — see `services/gateway/src/routes/bills.ts`'s
 * own header on `GET /api/bills` for why: the endpoint used to return a
 * hardcoded 100 rows with no way to ask for more, no way to know how many
 * existed, and a caller-uncontrolled payload sitting at 82% of the P2C3
 * §7.1.6 50,000-byte cap.
 */
/**
 * ★★ S-067 — the bounds the DECISION routes enforce, as the SERVER publishes them.
 *
 * `apps/officer-web/src/lib/reject-reason.ts` used to declare
 * `REJECT_REASON_MAX_LENGTH = 1000` and its own header called that out as *"the
 * FOURTH place that figure appears"*, kept in step by somebody remembering. It is
 * deleted; both screens that write a decision read this instead, from the read they
 * already make (`GET /api/bills` and `GET /api/bills/:reference` both publish it).
 *
 * ⚠ **OPTIONAL on the wire, and an absent block must NOT be defaulted to a
 * remembered number.** A screen served by an older gateway shows no ceiling and
 * relies on the server's refusal, which is safe; substituting 1,000 there would
 * recreate the finding in the one place nobody would look for it. The same shape
 * `BillTransition.actorIsSystem` uses for the same reason.
 *
 * ⚠ Declared with `Record`, matching the gateway's `FieldBounds`, so a field added
 * to the published set reaches the client with nothing to update here.
 */
export interface PublishedFieldBounds {
  readonly minLength?: number
  readonly maxLength?: number
  readonly pattern?: string
  readonly min?: number
}

export type DecisionFieldBounds = Readonly<Record<string, PublishedFieldBounds>>

/**
 * The maximum length the server published for a field, or `undefined`.
 *
 * ⚠ `undefined` is a real answer and the callers treat it as one — see
 * `rejectReasonProblem`, which bounds nothing when it is absent rather than
 * inventing a ceiling.
 */
export function publishedMaxLength(
  validation: DecisionFieldBounds | undefined,
  field: string,
): number | undefined {
  return validation?.[field]?.maxLength
}

export interface BillsListResponse {
  readonly bills: readonly Bill[]
  /** Rows this scope can see in total — under the SAME predicate as `bills`, never a wider count. */
  readonly total: number
  /** Opaque; pass back as `cursor` to fetch the next page. `null` on the last page. */
  readonly nextCursor: string | null
  /**
   * ★★ S-067 — the decision bounds this route enforces, published so the screen
   * renders the real rule and holds no copy. Optional: an older gateway sends
   * none, and the screen then shows no ceiling rather than a remembered one.
   */
  readonly validation?: DecisionFieldBounds
  /**
   * ★★★ WAVE 11 — the status filter the SERVER actually applied, echoed back.
   *
   * ⚠ **Load-bearing for what a screen may claim, not decoration.** `total` is now
   * the FILTERED population, so a screen cannot otherwise distinguish "3 paused
   * bills out of 3 paused bills" from "a scope that holds 3 bills at all" — and a
   * completeness claim is exactly what `AttentionPage` renders from these two
   * numbers. Same discipline as `GET /api/audit`'s `asOf`: publish the bound you
   * used, so the screen states a fact rather than an assumption.
   *
   * ⚠ Optional, and absence means UNFILTERED rather than unknown: an older gateway
   * sends none, and it also had no filter to apply. A screen that sent `?status=`
   * to a gateway which ignored it would see its own filter missing from the echo —
   * which is the only way a client can tell "narrowed" from "accepted and
   * discarded" across a version skew.
   */
  readonly appliedStatusFilter?: readonly string[]
}

/**
 * ★★ WAVE 8 (Task 2) — `GET /api/bills/status-counts` (S-029).
 *
 * The whole-population, scope-filtered count of `app.bill` by status. It exists
 * because the dashboard's four breakdown tiles were `rows.filter(...).length` over
 * ONE page of {@link BillsListResponse}, which is arithmetically wrong the moment a
 * scope holds more bills than the page clamp — and the four then did not sum to the
 * `total` printed above them.
 *
 * ⚠ **`byStatus` reports what the database returned and classifies nothing.** A
 * status with no rows in this scope is ABSENT, not zero. Which statuses mean
 * "awaiting a level 1 decision" stays exactly where it already lives —
 * `pendingDecision()` in `lib/authority.ts`, the one definition the queue
 * membership, the dashboard tiles and the button gate all read. See
 * `BillStatusCounts` in `services/billing/src/bill-repository.ts` for why the
 * server deliberately does not answer the four figures itself.
 */
export interface BillStatusCountsResponse {
  /** Bills this scope can see — the SUM of `byStatus`, from the same statement. */
  readonly total: number
  /** Keyed by `app.bill.status`. A status with no rows is absent, never `0`. */
  readonly byStatus: Readonly<Record<string, number>>
}

/**
 * WHERE A SUBSIDY RATE CAME FROM, in the SERVER'S OWN WORDS.
 *
 * Published by `services/gateway/src/rate-provenance.ts` from
 * `app.ward_rule_version.note`. **This app contributes the placement and not a
 * syllable of the text** — the note is rendered verbatim by
 * `components/RateProvenance.tsx`.
 *
 * ⚠ A screen-side "SYNTHETIC" string would be a second copy of a claim about the
 * database, and it could not tell a fabricated CPW/FAW/CWM rate from one PSD
 * authors in answer to Q-34: it would either label both or neither. That is the
 * defect class `published-bounds.test.ts` is the census for.
 *
 * `null`/absent means **nothing claims this rate is fabricated**, and nothing is
 * shown. Every MSO and CCS bill is in that state, because those rates live in a
 * different table this disclosure never reads.
 */
export interface RateProvenance {
  readonly ruleId: string
  readonly version: number
  /** Rendered verbatim. Never truncated, re-worded or prefixed by this app. */
  readonly note: string
}

/**
 * `POST /api/bills/:reference/compute`, `.../approve`, `.../approve-l2`,
 * `.../reject` — the four WRITES, each answering with the bill as it now stands.
 *
 * ⚠ **Deliberately WITHOUT `rateProvenance`, and that absence is a guard.**
 * Those four routes do not publish it, so a screen cannot render a disclosure
 * from a write response — it has to re-read `GET /api/bills/:reference`
 * ({@link BillDetailResponse}), which is what `BillDetailPage`'s
 * `result.reload()` already does after every one of them. If this type carried
 * the field, a future edit that rendered the POST body directly would compile
 * and would silently show no label on a freshly computed ward-scheme bill.
 */
export interface BillResponse {
  readonly bill: Bill
}

/**
 * ★★ WAVE 10 — `POST /api/bills/:reference/refer`.
 *
 * ⚠ Carries BOTH the id and the name of the colleague the server actually
 * recorded, so the confirmation on screen names the person the LEDGER names —
 * never the label the officer happened to click. Two colleagues can share a
 * display name; echoing the server's answer is what makes the confirmation
 * evidence rather than a repetition of the request.
 */
export interface ReferBillResponse {
  readonly bill: Bill
  readonly referredTo: {
    readonly userId: string
    readonly displayName: string
  }
}

/** One colleague who can be asked about a bill — `GET /api/bills/:reference/referral-candidates`. */
export interface ReferralCandidate {
  readonly userId: string
  readonly displayName: string
  readonly role: string
}

export interface ReferralCandidatesResponse {
  readonly candidates: readonly ReferralCandidate[]
  /**
   * ⚠ How many active users were CHECKED. Published beside the list because an
   * empty `candidates` with no denominator reads as "there are no other officers",
   * when what it means is "no other officer holds a grant over this agency" — a
   * different fact with a different remedy.
   */
  readonly considered: number
  readonly notes: readonly string[]
}

/** One bill a colleague has asked this officer to look at — `GET /api/bills/referred-to-me`. */
export interface ReferralToMe {
  readonly billReference: string
  readonly billStatus: string
  readonly amount: number
  readonly agencyCode: string
  readonly referredAt: string
  /** What the colleague asked. Never empty: the database refuses a REFER row without it. */
  readonly comment: string
  /** `null` only for a caller who may not read approver identity. */
  readonly referredByUserId: string | null
  readonly referredByDisplayName: string | null
}

export interface ReferralInboxResponse {
  readonly referrals: readonly ReferralToMe[]
  /** ⚠ True when the list hit its ceiling. A truncated inbox shown as a whole one is how an ask gets missed. */
  readonly truncated: boolean
  readonly notes: readonly string[]
}

/**
 * `GET /api/bills/:reference` — the bill and the provenance of the rate that
 * produced its money.
 *
 * (The route also returns `gracePeriod`; this app has never read it, so it is
 * not declared here rather than declared and ignored.)
 */
export interface BillDetailResponse {
  readonly bill: Bill
  readonly rateProvenance: RateProvenance | null
  /**
   * ★★★ THE DATE-OF-SERVICE RULE, RESOLVED BY THE SERVER ON BOTH OF THIS BILL'S
   * DATES so the screen can state the difference instead of a presenter claiming
   * it. `differs` is true only when both sides resolved AND they are different
   * periods — "no entitlement covers the submission date" is a different fact
   * and arrives as `unresolved`.
   *
   * ⚠ OPTIONAL, so an older gateway simply produces no sentence rather than a
   * wrong one. The screen must never infer the basis from the two dates itself:
   * only the server holds the entitlement register.
   */
  readonly entitlementBasis?: EntitlementBasis
  /**
   * ★★ S-067 — the decision bounds this route enforces, published so the screen
   * renders the real rule and holds no copy. Optional: an older gateway sends
   * none, and the screen then shows no ceiling rather than a remembered one.
   */
  readonly validation?: DecisionFieldBounds
}

/** One side of {@link EntitlementBasis} — the entitlement in force on one date. */
export interface ResolvedBasis {
  readonly scheme: string
  readonly wardClass: string
  readonly validFrom: string
  readonly validUntil: string | null
}

/**
 * What the officer was covered for on each of a bill's two dates.
 *
 * The bill is priced on {@link atService}. {@link atSubmission} is carried for
 * one reason only — to show what a system that validated on the submission date
 * would have reached instead — and is never used to compute anything.
 */
export interface EntitlementBasis {
  readonly atService: ResolvedBasis | null
  readonly atSubmission: ResolvedBasis | null
  readonly differs: boolean
  readonly unresolved: string | null
}

/** `GET /api/entitlement` — `EntitlementPeriod` from `services/entitlement/src/entitlement.ts`. */
export interface EntitlementPeriod {
  readonly officerId: string
  readonly scheme: string
  readonly window: { readonly from: string; readonly until: string | null }
  readonly wardClass: string
}

export interface EntitlementResponse {
  readonly entitlement: EntitlementPeriod
  /**
   * The service date the gateway actually resolved `entitlement` against —
   * ISO `YYYY-MM-DD` (I7). Not necessarily equal to whatever the query's
   * `serviceDate` looks like right now: this is the server's record of what it
   * resolved, so the client can render "as at" from the response rather than
   * from its own live input state, which may have changed since the request
   * that produced this result was sent.
   */
  readonly resolvedServiceDate: string
}

/**
 * ⚠ Re-exported from `lib/audit-outcome.ts`, not declared here.
 *
 * It WAS declared here, as `'ALLOWED' | 'DENIED' | 'ERROR'`, and the day
 * migration 009 added a fourth value (`REFUSED`) nothing failed to compile —
 * because `AuditEvent.outcome` arrives cast from JSON, so this union is a claim
 * about the server rather than a check on it. A `REFUSED` row rendered a badge
 * with an `undefined` label and no variant. The union now lives beside the
 * runtime guard that validates it, so a widened enum has one place to land.
 */
export type { AuditOutcome, AuditOutcomeReason } from './audit-outcome'

/** `GET /api/audit` — one row of `audit.event`. */
export interface AuditEvent {
  readonly id: string
  readonly occurredAt: string
  readonly actorUserId: string | null
  readonly actorRole: string
  readonly action: string
  readonly resourceType: string
  readonly resourceId: string | null
  readonly requestId: string
  readonly source: string
  /** ⚠ CAST, not validated, on arrival — read it through `isAuditOutcome`. */
  readonly outcome: string
  /**
   * `{ status, outcomeReason }` on every row written since migration 009, and
   * absent or partial on older ones. Left as `unknown` values deliberately:
   * `outcomeReasonOf`/`outcomeStatusOf` in `lib/audit-outcome.ts` are the only
   * readers, and they validate rather than assume.
   */
  readonly detail?: Record<string, unknown>
}

/**
 * ★★ WAVE 9 — the audit ARCHIVE surfaces (P2C1 §12.134 rotation, §12.135 storage
 * assessment). Four routes that had no screen at all until this wave; the RTM's
 * Rules section carried a `⚠ NO SCREEN:` declaration for each.
 *
 * ⚠ **No ciphertext and no IV, ever.** The route deliberately omits both from this
 * summary (see its own header), so the client type cannot carry them and a screen
 * cannot render them by accident.
 */
export interface ArchiveSegmentSummary {
  readonly id: string
  readonly fromEventId: string
  readonly toEventId: string
  readonly eventCount: number
  readonly fromOccurredAt: string
  readonly toOccurredAt: string
  readonly algorithm: string
  readonly encryptionKeyId: string
  readonly signingKeyId: string
  readonly contentDigest: string
  readonly signature: string
  readonly createdAt: string
  readonly createdBy: string | null
}

export interface ArchiveSegmentsResponse {
  readonly segments: readonly ArchiveSegmentSummary[]
  /**
   * ★★★ PAGED SINCE 2026-08-24 (WAVE 18) — `atCap: boolean` until then, for the
   * reason `SettlementBatchListResponse.paging` sets out.
   *
   * ⚠★ **The demonstration estate is the boundary case**: it holds exactly 100 sealed
   * segments, precisely the route's own limit, so the old response reported `atCap:
   * true` on every load and the screen said the archive list was a window — on the
   * screen whose whole purpose is proving that nothing is missing.
   */
  readonly paging?: {
    readonly limit: number
    readonly offset: number
    readonly total: number
    readonly returned: number
  }
  /** The server's own sentence about the page — rendered verbatim beside the pager. */
  readonly pagingNote?: string
}

/**
 * ⚠ **FOUR independent verdicts, never folded into one boolean**, and the route's
 * own header says why: a segment can fail signature verification and still decrypt
 * (proving the signature caught something GCM's tag did not, or the reverse), and
 * collapsing them would hide the one distinction an incident review needs. The
 * screen renders all four for the same reason.
 */
export interface ArchiveVerification {
  readonly segmentId: string
  readonly signatureValid: boolean
  readonly decryptable: boolean
  readonly contentDigestValid: boolean
  /** `undefined` when decryption failed — there is then no covered-id list to check. */
  readonly coveredEventsStillQueryable: boolean | undefined
  readonly eventCount: number
  readonly fromEventId: string
  readonly toEventId: string
}

export interface ArchiveVerifyResponse {
  readonly verification: ArchiveVerification
}

export interface ArchiveStorageStatus {
  readonly eventTableBytes: number
  readonly archiveTableBytes: number
  readonly backlogCount: number
  readonly totalEventCount: number
  readonly level: 'OK' | 'WARNING' | 'CRITICAL'
  readonly message: string
}

export interface ArchiveStatusResponse {
  readonly status: ArchiveStorageStatus
}

/**
 * `rotated: false` is a NORMAL answer, not an error — an empty or too-small backlog
 * has nothing to seal, and the route says which with a reason and the backlog size.
 */
/**
 * ★★★ WAVE 13 (P9) — whether the estate EVENT was raised, beside whatever the
 * operation itself did.
 *
 * ⚠ **`raised: true` means RECORDED, never DELIVERED**, and the screen must say
 * so in those words. `app.notification_log` carries the event; whether anybody
 * receives it depends on a `app.notification_rule` naming the event type (none
 * is seeded in any environment this repository ships) and on the outbox running
 * afterwards. A screen that rendered "alert sent" from this field would make the
 * same claim `050_notification.sql`'s honesty column exists to refuse.
 */
export type EstateNotificationOutcome =
  | { readonly raised: true }
  | { readonly raised: false; readonly reason: string }

export type ArchiveRunResponse =
  | { readonly rotated: false; readonly reason: string; readonly backlog: number }
  | {
      readonly rotated: true
      readonly segment: ArchiveSegmentSummary
      readonly notification: EstateNotificationOutcome
    }

/** ★★★ WAVE 13 (P9) — `POST /api/audit/archive/alerts/sweep`. */
export interface ArchiveAlertSweepResponse {
  readonly status: ArchiveStorageStatus
  readonly notification: EstateNotificationOutcome
}

export interface AuditListResponse {
  readonly events: readonly AuditEvent[]
  /**
   * ★★ The instant the server answered — the anchor a multi-page walk pins.
   *
   * ⚠ There is deliberately NO `total` here. `audit.event` is the largest
   * append-only table in the product and a filtered `count(*)` over it on every
   * page view is what P2C14 §6.1.1's 1 s p95 does not survive. The screen asks
   * for one row more than it shows to learn whether a next page exists, and says
   * "page 3" rather than inventing "of 9".
   */
  readonly asOf: string
  /** Why an unpinned walk drifts, in the server's own words. */
  readonly pagingNote: string
}

/** `GET /api/bills/:reference/transitions` — one row of `app.bill_transition`. */
/**
 * ★★ WAVE 10 — FOUR ADDED, and one of them was a live gap rather than new work.
 *
 * ` REVERSED` has existed since `052_billing_saga_reversal.sql` and was never in
 * this list, so the ledger rendered a real saga compensation under the words
 * *"this build does not recognise this transition"* — on the one screen a reviewer
 * opens to investigate a reversal. `REQUEST_INFO`, `RESUME` and `REFER` are 060's
 * (Annex A 37/38). All four now have a headline, an effect sentence and a tone.
 *
 * ⚠ `BillTransition.transition` stays `BillTransitionKind | string` even so. The
 * value crosses JSON and the compiler has no opinion on it, and the point of the
 * open type is that a FIFTH value renders its own name rather than a blank line —
 * which is exactly the state REVERSED was in until now.
 */
export const BILL_TRANSITIONS = [
  'APPROVE_L1',
  'APPROVE_L2',
  'REJECT',
  'REVERSED',
  'REQUEST_INFO',
  'RESUME',
  'REFER',
] as const
export type BillTransitionKind = (typeof BILL_TRANSITIONS)[number]

/**
 * ⚠ `reason` is non-null for, and only for, `REJECT` — the database enforces it
 * (`CHECK ((transition = 'REJECT') = (reason IS NOT NULL))`, 008). Typed as
 * nullable on every kind anyway, because this is the client and the CHECK is not
 * a type; a renderer that assumed the correlation would break on the first row
 * that violated it instead of showing it.
 *
 * ⚠ `actorDisplayName` is carried alongside `actorUserId` on purpose: two
 * officers can share a display name, so the id is what identifies and the name
 * is what is read.
 *
 * ⚠ `actorUserId`/`actorDisplayName` are NULLABLE, and `null` means WITHHELD —
 * never "unknown". The gateway does not send the deciding officer's id or name
 * to a role that has no need for it (P2C11 §3.13 sanitises user IDs out of
 * anything reaching a third party, and the four counterparty groups are third
 * parties). See `mayReadApproverIdentity` in `packages/auth/src/scope.ts`.
 *
 * ⚠ I4 (final review) — `level` and `actorRole` are ALSO nullable, for a
 * DIFFERENT reason than withholding: `REVERSED` (the saga's system
 * compensating action, `services/billing/src/intake-effects.ts`'s
 * `compensate()`) carries no authorisation level at all and may carry no
 * human actor — `app.bill_transition.level`/`.actor_user_id` are genuinely
 * `NULL` for that row, not redacted. `services/billing/src/transition-
 * repository.ts` used to read them as `number`/`string` unconditionally,
 * which rendered a forbidden `level: 0` and the literal string `"null"` for
 * `actorRole` on exactly this row. Kept nullable here too so a future
 * renderer cannot make the same mistake by trusting a type this client
 * controls.
 */
export interface BillTransition {
  readonly id: string
  readonly transition: BillTransitionKind | string
  readonly level: number | null
  readonly actorUserId: string | null
  readonly actorDisplayName: string | null
  readonly actorRole: string | null
  /**
   * ★ TRUE when no human decided this — a saga `REVERSED` row. Sent by the
   * gateway rather than inferred here: a service actor now carries a real role
   * (`'PRS_SERVICE'`), so `actorRole === null` stopped meaning "the system did
   * it" the day the ledger began recording roles.
   *
   * ⚠ Optional on the wire so a screen served by an older gateway degrades to
   * the previous behaviour rather than crashing.
   */
  readonly actorIsSystem?: boolean
  readonly reason: string | null
  readonly occurredAt: string
  /**
   * ★★ WAVE 9 — Annex A 37/38's approval COMMENT. `null` when none was left.
   *
   * ⚠ NOT `reason` and never a fallback for it: `reason` is the ground on which a
   * bill was REFUSED and the ledger's own constraint makes it required for exactly
   * REJECT and REVERSED. This is a remark, on any transition.
   *
   * ⚠ Optional on the wire, so a screen served by an older gateway renders no
   * comment rather than crashing.
   */
  readonly officerComment?: string | null
  /**
   * ★★ WAVE 10 — THE RETURN ADDRESS on a `REQUEST_INFO` entry: the queue this
   * pause will put the bill back into. `null`/absent on every other kind.
   *
   * ⚠ On screen this is the difference between *"paused, and it comes back needing
   * the senior approval"* and *"paused, and it is nearly done"* — which an officer
   * needs BEFORE they write to the HSP, not after.
   */
  readonly resumeStatus?: string | null
  /**
   * ★★ WAVE 10 — the colleague a `REFER` entry names.
   *
   * ⚠ `null` means EITHER "not a referral" or "withheld from your role" — the
   * gateway redacts this with the same gate as `actorUserId`, because a referral
   * names an individual just as surely as a decision does. The two cases are
   * indistinguishable on purpose: a caller who may not see who decided must not be
   * able to infer who was asked.
   */
  readonly referredToUserId?: string | null
  readonly referredToDisplayName?: string | null
}

export interface BillTransitionsResponse {
  readonly transitions: readonly BillTransition[]
  /**
   * True when the ledger holds more rows than the gateway will return
   * (`MAX_TRANSITIONS_PER_BILL`). These rows read oldest-first, so a truncated
   * list is missing its NEWEST entries — the ones the reader came for. Rendered
   * as a warning rather than swallowed.
   */
  readonly truncated?: boolean
}

/** The shape of every error body the gateway sends: `reply.code(N).send({ message })`. */
export interface ApiErrorBody {
  readonly message: string
}

// ─────────────────────────────────────────────────────────────────────────
// ★ TASK 11 — `GET /api/records`, Annex A stories 9 and 10's enquiry.
// ─────────────────────────────────────────────────────────────────────────

/** One prior correction against the bill this record's effect names — `null` under the same rule `amountCents` follows. */
export interface RecordAdjustment {
  readonly seq: number
  readonly indicator: 'UA' | 'DA'
  readonly deltaAmountCents: number
}

/**
 * `GET /api/records` and its own row shape — `RecordEnquiryRow` in
 * `services/transfer/src/inbound-file-repository.ts`. Every `bill*` field
 * (including `adjustments`) is `null` for a REJECTED record and ALSO for an
 * ACCEPTED one whose bill this scope may not read — the two are deliberately
 * indistinguishable, the same "not yours vs not there" discipline
 * `GET /api/bills/:reference`'s 404 already follows.
 */
export interface EnquiryRecord {
  readonly id: string
  readonly fileId: string
  readonly counterpartyCode: string
  readonly channelFileName: string
  readonly receivedDate: string
  readonly receivedAt: string
  readonly processedDate: string
  readonly ordinal: number
  readonly outcome: 'ACCEPTED' | 'REJECTED'
  /** Safe to show — the remedy is derived from this at render time, never stored. */
  readonly reasonKind: string | null
  readonly sourceReference: string | null
  readonly effectReference: string | null
  readonly billStatus: string | null
  readonly settlementState: string | null
  readonly serviceDate: string | null
  readonly amountCents: number | null
  readonly subsidyCents: number | null
  readonly copayCents: number | null
  readonly paidAmountCents: number | null
  readonly appliedRule: AppliedRule | null
  readonly adjustments: readonly RecordAdjustment[] | null
}

/**
 * ★★★ WAVE 27 — the interface and batch failure counts behind the dashboard's
 * alerts tile, exactly as the SERVER computed them.
 *
 * ⚠ **Every number here is the server's.** The tile renders these and derives no
 * total of its own — S-011's rule, and the reason `BillStatusCountsResponse`
 * above exists at all. A share ("1 of 77 deliveries") is a presentation of two
 * server numbers, never a third number invented in the browser.
 *
 * ⚠ **`fileReasons` and `recordReasons` SUM to `filesRejected` / `recordsRejected`**,
 * because the repository counts a rejection with no stored reason under
 * `REASON_NOT_RECORDED` rather than dropping it. A breakdown that does not sum to
 * the figure printed beside it is the S-029 defect, and it is prevented in SQL
 * rather than reconciled here.
 */
export interface InterfaceAlertCountsResponse {
  /** Every delivery this scope can see, whatever became of it. The denominator. */
  readonly filesSeen: number
  /** Deliveries refused whole, at the interface. */
  readonly filesRejected: number
  /** Why they were refused, `count` descending then reason ascending — a stable order. */
  readonly fileReasons: readonly InterfaceAlertReason[]
  /** Every record inside every delivery this scope can see. */
  readonly recordsSeen: number
  /** Records refused inside a delivery that was itself accepted. */
  readonly recordsRejected: number
  /** Why they were refused, in the same stable order. */
  readonly recordReasons: readonly InterfaceAlertReason[]
}

/** One refusal reason and how many times it occurred, as the server grouped it. */
export interface InterfaceAlertReason {
  readonly reasonKind: string
  readonly count: number
}

/**
 * ★★★ WAVE 27 — the dashboard's time series, exactly as the SERVER computed it.
 *
 * ⚠ **`window.anchoredTo` is the server's answer and the screen must print it.**
 * On the seeded estate the latest service date sits weeks ahead of the machine's
 * clock, so a screen that assumed `to` meant "today" would state a false period
 * basis on every chart (`ux-direction.md` §5.3 rule 10).
 */
export interface DashboardSeriesResponse {
  readonly window: {
    readonly from: string | null
    readonly to: string | null
    readonly days: number
    readonly anchoredTo: 'LATEST_ACTIVITY' | 'FUTURE_DATED_ONLY' | 'NO_DATA'
    readonly latestServiceDate: string | null
  }
  readonly billsPerDay: readonly { readonly date: string; readonly count: number }[]
  /** The same-length window immediately before, so a delta has a stated baseline. */
  readonly totals: { readonly current: number; readonly previous: number }
  readonly transitionsPerDay: readonly {
    readonly date: string
    readonly approveL1: number
    readonly approveL2: number
    readonly reject: number
  }[]
  /** The mix of bill types INSIDE the charted window — see the server's own note. */
  readonly billTypeMix: readonly { readonly billType: string; readonly count: number }[]
}

/**
 * ★★★ WAVE 27 — what `POST /api/reports/email` answers.
 *
 * ⚠ **`note` IS PROSE THE SERVER WROTE, and the screen renders it verbatim.** It
 * describes what this deployment will actually do with the queued message. In
 * every environment this repository ships that is "nothing will be sent, no mail
 * transport is configured" — and a browser that replaced it with "Sent" would be
 * telling the one lie this product's notification design exists to prevent.
 */
export interface EmailReportResponse {
  readonly deliveryId: string
  readonly recipient: string
  readonly attachment: { readonly filename: string; readonly mediaType: string; readonly bytes: number }
  /** `none` | `simulated` | `live`, as the gateway reports it on `/api/me`. */
  readonly notificationDelivery: string
  readonly queuedAt: string
  readonly note: string
}

/** ★ TASK 11. Same shape as `BillsListResponse` — `total`/`nextCursor` under the identical scope predicate as `records`. */
export interface RecordsListResponse {
  readonly records: readonly EnquiryRecord[]
  readonly total: number
  readonly nextCursor: string | null
}

// ─────────────────────────────────────────────────────────────────────────
// ★★ WAVE 1a — `GET /api/inbound/channels` and the upload, Annex A 2-4.
// ─────────────────────────────────────────────────────────────────────────

export interface InboundChannelSummary {
  readonly channelId: string
  readonly counterpartyCode: string
  readonly counterpartyKind: string
}

/**
 * ⚠ Every bound and every capability here comes from the SERVER, and the page
 * renders no number of its own. A build is copied between deployments far more
 * casually than configuration is — the same argument `identityProvider` and
 * `settlementDispatch` already carry — so a bound baked into this bundle would
 * keep claiming the old one after the server's changed.
 */
export interface InboundChannelsResponse {
  readonly channels: readonly InboundChannelSummary[]
  /**
   * How many channels this DEPLOYMENT wired, before the caller's grants
   * narrowed the list. It is what lets the page distinguish "this deployment
   * has no upload surface" from "you may not use the one it has" — two
   * sentences that lead an officer to two different next actions.
   */
  readonly configuredChannelCount: number
  readonly maxUploadBytes: number
  readonly maxUploadRecords: number
  readonly contentType: string
  readonly nameHeader: string
  /** `false` on every deployment this repository ships (P2C11 §12.17-§12.19, Bucket B). */
  readonly malwareScanning: boolean
  readonly malwareScanningNote: string
  readonly bulkDeliveryNote: string
}

export interface InboundUploadResponse {
  readonly channelId: string
  readonly counterpartyCode: string
  /** `null` when the delivery was never claimed — UNATTRIBUTABLE or NOT_IN_SCOPE. */
  readonly fileId: string | null
  readonly name: string
  readonly outcome:
    | 'PROCESSED'
    | 'REJECTED'
    | 'DUPLICATE'
    | 'UNATTRIBUTABLE'
    | 'NOT_IN_SCOPE'
    | 'LEASED_ELSEWHERE'
    | 'INTERRUPTED'
  readonly reasonKind: string | null
  readonly parsed: number
  readonly accepted: number
  readonly rejected: number
  readonly bytes: number
  readonly malwareScanned: boolean
  readonly malwareScanNote: string
}

/**
 * ★★ WAVE 1c. What a composed correction produced — Annex A stories 3 and 4.
 *
 * ⚠ The same `outcome`/`accepted`/`rejected` shape an upload returns, because
 * it IS an upload: the server rendered the fields into a one-record delivery and
 * ran the identical pipeline. A `PROCESSED` file with `rejected: 1` means the
 * delivery was read and the correction was refused BY THE GRAMMAR — two
 * different sentences, and the screen must not collapse them.
 */
export interface InboundAdjustmentResponse {
  readonly channelId: string
  readonly counterpartyCode: string
  /** Derived on the server from the sign of the delta. The caller never sends it. */
  readonly indicator: 'UA' | 'DA'
  readonly fileId: string | null
  readonly outcome: InboundUploadResponse['outcome']
  readonly reasonKind: string | null
  readonly parsed: number
  readonly accepted: number
  readonly rejected: number
  readonly malwareScanned: boolean
  readonly malwareScanNote: string
}

// ─────────────────────────────────────────────────────────────────────────
// ★ TASK 12 — `GET /api/contacts`, Annex A stories 11 and 12.
// ─────────────────────────────────────────────────────────────────────────

export const CONTACT_ROLES = ['BILLING', 'TECHNICAL', 'MANAGEMENT'] as const
export type ContactRoleValue = (typeof CONTACT_ROLES)[number]

/** `GET /api/contacts` — one row of `app.counterparty_contact`. */
export interface CounterpartyContact {
  readonly id: string
  readonly counterpartyCode: string
  readonly counterpartyKind: 'HSP' | 'PAYMENT' | 'RECOVERY' | 'ENTITLEMENT'
  readonly contactName: string
  readonly contactRole: ContactRoleValue
  readonly phone: string
  readonly email: string
  readonly availabilityHours: string | null
  readonly isActive: boolean
  /** Canonical UTC instant — story 11's own "last updated date". */
  readonly updatedAt: string
}

export interface ContactsListResponse {
  readonly contacts: readonly CounterpartyContact[]
  readonly total: number
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 6, TASK 18 — Annex A story 26, `GET /api/administration/ward-classes`.
// ─────────────────────────────────────────────────────────────────────────

/** One row of `app.ward_class` — see `services/administration/src/ward-class-repository.ts`. */
export interface WardClass {
  readonly id: string
  readonly code: string
  readonly label: string
  readonly category: string
  readonly rankOrder: number
  readonly description: string | null
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly note: string | null
}

/**
 * ★★★ WAVE 13 (P8) — Annex A 19-21's ward SUBSIDY RATE register
 * (`app.ward_rule_version`). A sibling of `WardClass` above and deliberately a
 * separate type: that one is CLASSIFICATION and carries no rate; this one is the
 * money a CPW/FAW/CWM bill is priced by.
 */
/**
 * ★★★ WAVE 13 (P10) — P2C1 §12.141's retention register
 * (`app.retention_policy`).
 */
/**
 * ★★★ WAVE 13 (P7) - one re-proration of an already-computed bill, or one
 * referral of a bill that needed one and could not have it.
 *
 * P2C1 §12, Recovery Agency interface row 7: "Alert file on re-prorated HSP
 * bills due to changes in officers' entitlement records".
 */
export interface BillReproration {
  readonly id: string
  readonly billReference: string
  /**
   * ⚠ `REFERRED` means PRS did NOT change the split. The two are not degrees of
   * the same thing: one is a money movement that happened, the other is one that
   * is owed and needs a person.
   */
  readonly outcome: 'REPRORATED' | 'REFERRED'
  readonly priorSubsidy: number
  readonly priorCoPayment: number
  readonly newSubsidy: number
  readonly newCoPayment: number
  /** `<scheme>~<ward>~<validFrom>~<validUntil or "open">`. Readable on purpose. */
  readonly priorEntitlementDigest: string | null
  readonly newEntitlementDigest: string
  readonly priorRule: { readonly id: string | null; readonly version: number | null; readonly rate: number | null }
  readonly newRule: { readonly id: string; readonly version: number; readonly rate: number }
  readonly cause: string
  /** Why a REFERRED bill was left alone. `null` on a REPRORATED row. */
  readonly referralReason: string | null
  readonly computedAt: string
  readonly recordedAt: string
}

export interface BillReprorationsResponse {
  readonly reference: string
  readonly reprorations: readonly BillReproration[]
}

/**
 * ★★★ WAVE 14 — `POST /api/bills/reprorate-sweep`, the estate-wide operator
 * action, now reachable from a screen.
 *
 * ⚠ **Every one of these eleven counts is rendered, and none may be dropped as
 * uninteresting.** A console that showed only `reprorated` and `referred` would
 * report "0 and 0" identically for an estate where nothing changed, an estate
 * where nothing was assessable, and a run that never reached the bills that
 * moved. Those are three different answers.
 */
export interface ReprorationSweepCounts {
  readonly examined: number
  readonly eligible: number
  /** Bills past this run's bound. ⚠ Unexamined, NOT unchanged. */
  readonly notReached: number
  readonly reprorated: number
  readonly referred: number
  /** Computed before PRS recorded which entitlement produced the split. Nothing can be said about them. */
  readonly unassessable: number
  readonly unchanged: number
  /** The entitlement moved and the split did not — a re-grade within the same rate. */
  readonly entitlementChangedNoMoneyMoved: number
  /** Cannot be priced today at all (no rule in force, a cap with no parameter). */
  readonly refused: number
  /** Changed under the write and was left untouched. */
  readonly moved: number
  /** This exact referral has already been made and the RA has already been told. */
  readonly alreadyReferred: number
}

/** One bill whose money moved, or was owed a move PRS refused to make. */
export interface ReprorationSweepEntry {
  readonly reference: string
  readonly officerId: string
  readonly agencyCode: string
  readonly outcome: 'REPRORATED' | 'REFERRED'
  readonly cause: string
  readonly priorEntitlement: string
  readonly newEntitlement: string
  readonly priorSubsidy: number
  readonly priorCoPayment: number
  readonly newSubsidy: number
  readonly newCoPayment: number
  readonly referralReason: string | null
}

export interface ReprorationSweepResponse {
  readonly sweepId: string
  readonly dryRun: boolean
  readonly counts: ReprorationSweepCounts
  readonly entries: readonly ReprorationSweepEntry[]
  /** The cursor this run started after, or `null` when it started at the beginning. */
  readonly afterReference: string | null
  /**
   * ★★ Where a continuation must start, or `null` when this run reached the end.
   *
   * ⚠ Without this a bounded sweep could never reach the tail of an estate
   * larger than the route's own limit ceiling — see the route's comment.
   */
  readonly nextAfterReference: string | null
  /** The server's own sentence about what this run did and did not reach. Rendered verbatim. */
  readonly boundary: string
  /** The server's own "there is no scheduler" sentence. Rendered verbatim. */
  readonly scheduler: string
}

/**
 * ★★★ WAVE 14 — `POST /api/reproration-alerts/:counterpartyCode`.
 *
 * ⚠ `NOTHING_TO_EMIT` is a 200 and carries `message` instead of a file: an
 * empty artefact is refused before a row is created, because a Recovery Agency
 * receiving one cannot tell a quiet day from a failed run.
 */
export interface ReprorationAlertResponse {
  readonly outcome: string
  readonly counterpartyCode: string
  readonly businessDate: string
  readonly message?: string
  readonly fileId?: string
  readonly recordCount?: number
  /**
   * ⚠ The trailer of an alert file is the NET MOVEMENT in officer co-payments,
   * not a payable. It can legitimately be negative or zero.
   */
  readonly netCoPaymentDeltaCents?: number
  readonly contentDigest?: string
  readonly boundary?: string
}

export interface RetentionPolicy {
  readonly id: string
  readonly subject: string
  readonly disposition: string
  /** `null` if and only if the disposition is RETAIN_INDEFINITELY. */
  readonly retentionMonths: number | null
  /** The Authority's own standard, cited. `null` means none was — which is every row today. */
  readonly standardReference: string | null
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly closedAt: string | null
  readonly note: string | null
}

/**
 * ★ What disposal MEANS for one record class, as three distinguishable answers.
 *
 * ⚠ *Not performed* and *cannot be performed* are different answers to a
 * compliance question. Collapsing them would let a reader conclude either that
 * PRS quietly deletes audit rows or that a DISPOSE_AT_EXPIRY policy over
 * `app.bill` is being honoured. Neither is true, and for different reasons.
 */
export type DisposalPostureView =
  | { readonly kind: 'NO_POLICY' }
  | { readonly kind: 'NOTHING_DUE_BY_POLICY'; readonly disposition: string }
  | { readonly kind: 'DUE_BUT_NOT_PERFORMED'; readonly reason: string }
  | { readonly kind: 'IMPOSSIBLE_BY_CONSTRUCTION'; readonly reason: string }

export interface RetentionSubjectAssessment {
  readonly subject: string
  readonly table: string
  readonly describes: string
  /** ⚠ The DATABASE refuses DELETE on this class from every role, superuser included. */
  readonly immutable: boolean
  readonly policy: RetentionPolicy | null
  readonly rowCount: number
  readonly oldestAt: string | null
  /**
   * ⚠ `null` when NO POLICY IS IN FORCE — never 0. Zero would claim nothing is
   * past retention, which is a statement about an interval nobody has defined,
   * and the screen renders the two differently for exactly that reason.
   */
  readonly pastRetention: number | null
  readonly disposal: DisposalPostureView
}

export interface RetentionResponse {
  readonly assessment: {
    readonly assessedAt: string
    readonly subjects: readonly RetentionSubjectAssessment[]
    readonly policiesInForce: number
    /** ★ The SERVER's standing sentence — a claim about the database, never the screen's. */
    readonly standing: string
  }
  readonly policies: readonly RetentionPolicy[]
  readonly subjects: readonly string[]
  readonly dispositions: readonly string[]
  readonly validation: PublishedBounds<'retentionMonths' | 'standardReference' | 'note'>
}

export interface WardRule {
  /** `<SCHEME>:<WARD>`, e.g. `CPW:B1`. Not unique on its own — see `version`. */
  readonly id: string
  /**
   * ⚠ Part of the key, never decoration. `app.bill` stores `applied_rule_id` AND
   * `applied_rule_version`, and a superseding v2 at the same id is a DIFFERENT
   * rate with its own provenance — `rate-provenance.ts`'s whole design rests on
   * the pair naming exactly one rate forever.
   */
  readonly version: number
  readonly scheme: 'CPW' | 'FAW' | 'CWM'
  readonly wardClass: string
  /** 0..10000. 6500 is 65.00%. */
  readonly subsidyBasisPoints: number
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly closedAt: string | null
  /** The authoring officer's own words, verbatim. `null` for a migration or seed row. */
  readonly note: string | null
  /**
   * ⚠ A BOOLEAN, never the officer's id (P2C11 §3.13). The question a reader of
   * this register has is whether a PERSON typed the number — which is what
   * decides whether a provenance note was mandatory for it.
   */
  readonly authored: boolean
}

export interface WardRuleListResponse {
  readonly wardRules: readonly WardRule[]
  readonly total: number
  readonly schemes: readonly ('CPW' | 'FAW' | 'CWM')[]
  readonly validation: PublishedBounds<'wardClass' | 'subsidyBasisPoints' | 'note'>
  /**
   * ★ Present ONLY when the register is empty under the caller's own filter.
   *
   * ⚠ The SERVER's sentence, not the screen's. It is a claim about the DATABASE
   * — that no rate is in force and why — and a `.tsx` copy of it would keep
   * asserting that after the Authority supplies one. Its absence is how the
   * screen knows to stop saying it.
   */
  readonly emptyRegisterNotice?: string
}

export interface WardClassListResponse {
  readonly wardClasses: readonly WardClass[]
  readonly total: number
  /** ★★ WAVE 8 (Task 3) — the bounds this catalogue's route enforces, published as data. */
  readonly validation: PublishedBounds<'code' | 'label' | 'category' | 'rankOrder' | 'note'>
}

/**
 * ★★★ WAVE 12 (P4) — THE SERIES SHAPE **EVERY** ADMINISTERED CATALOGUE ANSWERS
 * WITH, declared once.
 *
 * All nine single- and composite-key series routes return `{ …the key(s), entries }`
 * — checked against each route rather than assumed. So the useful client type is
 * generic in the row, and the key fields are ignored: the caller already knows
 * which code it asked for, and reading it back would be the client trusting the
 * server to echo its own input.
 *
 * ⚠ `entries` ONLY. A per-catalogue interface for each of the thirteen would be
 * thirteen places for one shape to drift, which is the S-011 pattern this codebase
 * has paid for once already.
 */
export interface CatalogueSeriesResponse<T> {
  readonly entries: readonly T[]
}

/**
 * ★★★ WAVE 12 (P4) — ONE CODE'S WHOLE HISTORY, and the shape every administered
 * catalogue's series route already answers with.
 *
 * `GET /api/administration/ward-classes/:code` has returned this since Phase 6 and
 * **no screen had ever asked for it** — the RTM's Annex A 26–28 row: *"Not built on
 * the screens: a history/point-in-time (`?asRecordedAt=`) view of a past version —
 * the API supports it, the screen shows only the current"*. `WardClassesPage`'s own
 * header said the same thing about itself.
 *
 * ⚠ Entries are **oldest first**, as the repository returns them (`ORDER BY
 * effective_from`), and the screen does not re-sort: the reading order of a history
 * IS its order, and a sortable version list would let a reader put a supersession
 * before the version it superseded.
 */
// ★★★ WAVE 12 (P4) — `WardClassSeriesResponse` IS RETIRED, not renamed.
//
// It carried `code` beside `entries`, and `code` is the server echoing back the
// value the caller just put in the URL — so reading it is the client trusting the
// server to repeat its own input. Every one of the nine series routes returns the
// same `{ …key(s), entries }` shape, so `CatalogueSeriesResponse<T>` above covers
// all of them and there is one shape rather than thirteen that can drift apart.

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 6, TASK 18 — Annex A story 27, `GET /api/administration/subsystems`.
// ─────────────────────────────────────────────────────────────────────────

export const SUBSYSTEM_STATUSES = ['ACTIVE', 'INACTIVE', 'DECOMMISSIONED'] as const
export type SubsystemStatusValue = (typeof SUBSYSTEM_STATUSES)[number]

/** One row of `app.subsystem_info` — see `services/administration/src/subsystem-repository.ts`. */
export interface SubsystemInfo {
  readonly id: string
  readonly subsystemCode: string
  readonly name: string
  readonly description: string | null
  readonly integrationEndpoint: string | null
  readonly status: SubsystemStatusValue
  readonly operationalNote: string | null
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly note: string | null
}

export interface SubsystemListResponse {
  readonly subsystems: readonly SubsystemInfo[]
  readonly total: number
  /** ★★ WAVE 8 (Task 3) — the bounds this catalogue's route enforces, published as data. */
  readonly validation: PublishedBounds<'subsystemCode' | 'name' | 'note'>
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 6, TASK 19 — Annex A story 28, `GET /api/administration/agency-mappings`.
// ─────────────────────────────────────────────────────────────────────────

/** One row of `app.agency_provider_mapping` — see `services/administration/src/agency-mapping-repository.ts`. */
export interface AgencyMapping {
  readonly id: string
  readonly mappingAgencyCode: string
  readonly mappingAgencyKind: string
  readonly providerAgencyCode: string
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly note: string | null
}

export interface AgencyMappingListResponse {
  readonly mappings: readonly AgencyMapping[]
  readonly total: number
  /** ★★ WAVE 8 (Task 3) — the bounds this catalogue's route enforces, published as data. */
  readonly validation: PublishedBounds<'providerAgencyCode' | 'note'>
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 6, TASK 20 — Annex A story 31, `GET /api/administration/medical-scheme-codes`.
// ─────────────────────────────────────────────────────────────────────────

/** One row of `app.medical_scheme_code` — see `services/administration/src/medical-scheme-code-repository.ts`. */
export interface MedicalSchemeCode {
  readonly id: string
  readonly code: string
  readonly description: string
  readonly category: string
  readonly coverageDetails: string | null
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly note: string | null
}

export interface MedicalSchemeCodeListResponse {
  readonly schemeCodes: readonly MedicalSchemeCode[]
  readonly total: number
  /** ★★ WAVE 8 (Task 3) — the bounds this catalogue's route enforces, published as data. */
  readonly validation: PublishedBounds<'code' | 'description' | 'category' | 'note'>
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 6, TASK 21 — Annex A stories 29/30, `GET /api/administration/hsp-groups`
// and `GET /api/administration/hsp-group-memberships`.
// ─────────────────────────────────────────────────────────────────────────

export const HSP_GROUP_STATUSES = ['ACTIVE', 'INACTIVE'] as const
export type HspGroupStatusValue = (typeof HSP_GROUP_STATUSES)[number]

/** One row of `app.hsp_group` — see `services/administration/src/hsp-group-repository.ts`. */
export interface HspGroup {
  readonly id: string
  readonly code: string
  readonly name: string
  readonly description: string | null
  readonly serviceCategories: string | null
  readonly status: HspGroupStatusValue
  readonly operationalNote: string | null
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly note: string | null
}

export interface HspGroupListResponse {
  readonly groups: readonly HspGroup[]
  readonly total: number
  /** ★★ WAVE 8 (Task 3) — the bounds this catalogue's route enforces, published as data. */
  readonly validation: PublishedBounds<'code' | 'name' | 'note'>
}

/** One row of `app.hsp_group_membership` — see `services/administration/src/hsp-group-membership-repository.ts`. */
export interface HspGroupMembership {
  readonly id: string
  readonly hspAgencyCode: string
  readonly hspAgencyName: string
  readonly groupCode: string
  readonly serviceOfferings: string | null
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly note: string | null
}

export interface HspGroupMembershipListResponse {
  readonly memberships: readonly HspGroupMembership[]
  readonly total: number
  /** ★★ WAVE 8 (Task 3) — the bounds this catalogue's route enforces, published as data. */
  readonly validation: PublishedBounds<'groupCode' | 'note'>
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 6, TASK 22 — Annex A story 36, `GET /api/administration/mbim-records`.
// ─────────────────────────────────────────────────────────────────────────

export const MBIM_ENTITLEMENT_TYPES = ['OFFICER', 'DEPENDANT'] as const
export type MbimEntitlementTypeValue = (typeof MBIM_ENTITLEMENT_TYPES)[number]

/** One row of `app.mbim_record` — see `services/entitlement/src/mbim-admin-repository.ts`. */
export interface MbimRecord {
  readonly id: string
  readonly mbimNumber: string
  readonly officerId: string
  readonly entitlementType: MbimEntitlementTypeValue
  readonly currency: { readonly issuedOn: string; readonly supersededOn: string | null }
  readonly recordedAt: string
  readonly note: string | null
}

export interface MbimRecordListResponse {
  readonly records: readonly MbimRecord[]
  readonly total: number
  /** ★★ WAVE 8 (Task 3) — the bounds this catalogue's route enforces, published as data. */
  readonly validation: PublishedBounds<'mbimNumber' | 'note'>
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 6, TASK 23 — Annex A story 32, `GET /api/administration/exclusion-list`.
// ─────────────────────────────────────────────────────────────────────────

export const EXCLUSION_IDENTIFIER_TYPES = ['ECS_CARD', 'FOREIGN_ID'] as const
export type ExclusionIdentifierTypeValue = (typeof EXCLUSION_IDENTIFIER_TYPES)[number]

/**
 * One row of `app.exclusion_list_entry` — see `services/administration/src
 * /exclusion-list-repository.ts`. ⚠ `identifierValue` is MASKED to its last 4
 * characters unless the caller searched by the exact value (P2C11 §3.6) —
 * see that repository's own header.
 */
export interface ExclusionListEntry {
  readonly id: string
  readonly identifierType: ExclusionIdentifierTypeValue
  readonly identifierValue: string
  readonly exclusionCriteria: string | null
  readonly exclusionReason: string | null
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly note: string | null
}

export interface ExclusionListResponse {
  readonly entries: readonly ExclusionListEntry[]
  readonly total: number
  /** ★★ WAVE 8 (Task 3) — the bounds this catalogue's route enforces, published as data. */
  readonly validation: PublishedBounds<'identifierValue' | 'note'>
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 6, TASK 24 — Annex A story 33, `GET /api/administration/email-templates`.
// ⚠ TEMPLATE ADMINISTRATION ONLY — the outbox schema now exists
// (`app.notification_delivery`, `054_notification_delivery.sql`), but no
// transport is wired to it. See `041_email_template.sql`'s own header.
// ─────────────────────────────────────────────────────────────────────────

export const EMAIL_TEMPLATE_STATUSES = ['ACTIVE', 'INACTIVE'] as const
export type EmailTemplateStatusValue = (typeof EMAIL_TEMPLATE_STATUSES)[number]

/** One row of `app.email_template` — see `services/administration/src/email-template-repository.ts`. */
export interface EmailTemplate {
  readonly id: string
  readonly templateCode: string
  readonly templateName: string
  readonly subjectLine: string
  readonly bodyContent: string
  readonly variables: string | null
  readonly category: string | null
  readonly status: EmailTemplateStatusValue
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly note: string | null
}

export interface EmailTemplateListResponse {
  readonly templates: readonly EmailTemplate[]
  readonly total: number
  /** ★★ WAVE 8 (Task 3) — the bounds this catalogue's route enforces, published as data. */
  readonly validation: PublishedBounds<'templateCode' | 'templateName' | 'subjectLine' | 'bodyContent' | 'note'>
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 6, TASK 25 — Annex A story 34, `GET /api/administration/announcements`.
// ─────────────────────────────────────────────────────────────────────────

export const ANNOUNCEMENT_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH'] as const
export type AnnouncementPriorityValue = (typeof ANNOUNCEMENT_PRIORITIES)[number]

/**
 * One row of `app.announcement` — see `services/administration/src
 * /announcement-repository.ts`. ⚠ `effective.from`/`effective.until` ARE the
 * "publication date"/"expiration date" story 34 names.
 */
export interface Announcement {
  readonly id: string
  readonly announcementCode: string
  readonly title: string
  readonly content: string
  readonly priority: AnnouncementPriorityValue
  readonly targetAudience: string | null
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly note: string | null
}

export interface AnnouncementListResponse {
  readonly announcements: readonly Announcement[]
  readonly total: number
  /** ★★ WAVE 8 (Task 3) — the bounds this catalogue's route enforces, published as data. */
  readonly validation: PublishedBounds<'announcementCode' | 'title' | 'content' | 'note'>
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 6, TASK 26 — Annex A story 35, `GET /api/administration/special-agency-cases`.
// ─────────────────────────────────────────────────────────────────────────

/** One row of `app.special_agency_case` — see `services/entitlement/src/special-agency-case-repository.ts`. */
export interface SpecialAgencyCase {
  readonly id: string
  readonly caseCode: string
  readonly officerId: string
  readonly agencyCode: string
  readonly caseType: string
  readonly entitlementConfiguration: string | null
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly note: string | null
}

export interface SpecialAgencyCaseListResponse {
  readonly cases: readonly SpecialAgencyCase[]
  readonly total: number
  /** ★★ WAVE 8 (Task 3) — the bounds this catalogue's route enforces, published as data. */
  readonly validation: PublishedBounds<'caseCode' | 'caseType' | 'note'>
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 10 (Task 36) — `GET /api/bills/:reference/adjustments`, Annex A 3/4.
// ─────────────────────────────────────────────────────────────────────────

/** One split, as recorded — never re-derived on this screen. */
export interface Split {
  readonly amount: number
  readonly subsidy: number
  readonly coPayment: number
}

export interface BillAdjustment {
  readonly seq: number
  readonly indicator: 'UA' | 'DA'
  readonly delta: number
  readonly deltaSubsidy: number
  readonly deltaCoPayment: number
  readonly preceding: Split
  readonly resulting: Split
  readonly appliedRule: AppliedRule
  /** Set when today's rule set disagrees with the rate this adjustment reproduced from the original. */
  readonly resolutionDrift: boolean
  readonly sourceReference: string | null
}

export interface BillAdjustmentsResponse {
  readonly adjustments: readonly BillAdjustment[]
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 10 (Task 38) — `GET /api/bills/:reference/payments`, Annex A 7.
// ─────────────────────────────────────────────────────────────────────────

export type PaymentEventStatus = 'PAID' | 'PARTIALLY_PAID' | 'PENDING' | 'PAYMENT_REJECTED'

export interface BillPaymentEvent {
  readonly paidAmount: number
  readonly status: PaymentEventStatus
  readonly occurredAt: string
  readonly sourceReference: string | null
}

export interface BillPaymentsResponse {
  readonly payments: readonly BillPaymentEvent[]
  /** THEIR settlement axis (app.bill.settlement_state) — never our approval `status`. */
  readonly settlementState: string | null
  readonly paidAmount: number | null
  readonly currentAmount: number | null
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 10 (Task 37) — `GET /api/settlement-batches`, Annex A 5/6/8.
// ─────────────────────────────────────────────────────────────────────────

export type SettlementArtefactKind = 'PA_SETTLEMENT' | 'HSP_OUTCOME'
export type SettlementArtefactState = 'GENERATED' | 'PUBLISHED' | 'COLLECTED' | 'FAILED'

export interface SettlementBatch {
  readonly id: string
  readonly counterpartyCode: string
  readonly artefactKind: SettlementArtefactKind
  readonly layoutId: string
  readonly layoutVersion: number
  readonly businessDate: string
  readonly state: SettlementArtefactState
  readonly recordCount: number
  readonly totalPayableCents: number | null
  readonly totalSubsidyCents: number | null
  readonly totalCopayCents: number | null
  readonly contentDigest: string | null
  readonly attemptCount: number
  readonly publishedAt: string | null
  readonly channelFileName: string | null
  readonly inboundFileId: string | null
}

export interface SettlementBatchListResponse {
  readonly files: readonly SettlementBatch[]
  /**
   * ★★★ PAGED SINCE 2026-08-24 (WAVE 18) — and this field carried `atCap: boolean`
   * until then.
   *
   * ⚠ Wave 17 published the cap because it had moved this list's sort to the SERVER,
   * and a sort that decides WHICH rows a reader sees must not be silent. It declined a
   * `total` on the stated ground that a `count(*)` would place the surface inside
   * `list-reachability.test.ts`'s census, which would then correctly demand a walk that
   * wave was not building. The walk exists now, so the boolean is gone and these are
   * the same three figures every other offset-paged list in this app carries.
   */
  readonly paging?: {
    readonly limit: number
    readonly offset: number
    /** Rows this caller can see under the IDENTICAL predicate as the rows — never a wider count. */
    readonly total: number
    readonly returned: number
  }
  /** The server's own sentence about the page — rendered verbatim beside the pager. */
  readonly pagingNote?: string
  /**
   * ★★ WAVE 1b. Whether this caller may release anything at all, computed on
   * the SERVER from the same `emissionScopeOf` the SQL predicate is built from
   * — never a role compare in this bundle.
   */
  readonly mayEmit: boolean
  /**
   * The active Payment Agencies a settlement artefact may be addressed to.
   * ⚠ Not scope-filtered, and {@link SettlementBatchListResponse.addresseeNote}
   * is why: which PA settles which provider is Q-30 / S-027 and is unanswered.
   */
  readonly addressableCounterparties: readonly string[]
  /** The Q-30 gap, in the server's own words, so the screen states it rather than implying enforcement. */
  readonly addresseeNote: string
  /**
   * ★★ WAVE 8 (Task 8). The outcome leg's STANDING honesty notice — *"produced is
   * not sent"* — in the server's own words, so `SettlementBatchesPage` can show
   * it before anything is asked and in every state afterwards.
   *
   * ⚠ **Byte-identical to `POST /api/outcome-files`'s own
   * {@link OutcomeFileResponse.handoverNote}**: one exported constant in
   * `routes/outcome-files.ts`, served under two field names because the two
   * answer different questions (what this deployment does with an artefact, and
   * what just happened to the one you asked for).
   * `services/gateway/src/routes/settlement-batches.test.ts` asserts the equality
   * so an edit to one cannot become a second version of it.
   */
  readonly outcomeHandoverNote: string
}

/**
 * ★★★ WAVE 8 (Task 8). What ONE outcome-file production produced — Annex A story
 * 8's provider-facing artefact, `POST /api/outcome-files`.
 *
 * ⚠ **Two SUCCESS outcomes, and they are different facts rather than two words
 * for one.** `GENERATED` means this request rendered the artefact.
 * `ALREADY_GENERATED` means the delivery had already been answered and the fields
 * below describe THAT artefact, byte for byte — `UNIQUE (inbound_file_id)` is what
 * guarantees there is only ever one, and the route returns the stored one rather
 * than re-rendering, because a second differently-timed answer would leave a
 * provider holding two files with no way to tell which counts (ADR 0009
 * Decision 4). Both are 2xx and neither is an error; a screen that collapsed them
 * would tell an officer they had just produced something they had not.
 *
 * ⚠ The three REFUSALS are not here at all. They arrive as {@link ApiError} —
 * `400` (no id / not id-shaped), `403` (an identity party to no counterparty) and
 * `409 DELIVERY_NOT_ANSWERABLE` (the delivery's state refused it, `deliveryState`
 * in the error's `detail`) — each carrying the route's own sentence, which is what
 * the screen renders. Modelling a refusal as a success-shaped body is how a screen
 * ends up rendering "requested" over a delivery that can never have an outcome
 * file.
 */
export interface OutcomeFileResponse {
  readonly outcome: 'GENERATED' | 'ALREADY_GENERATED'
  /** The delivery this artefact answers — echoed back, so a screen never has to trust its own state. */
  readonly inboundFileId: string
  /** The provider, DERIVED from the delivery inside the emission's own SQL and never a request parameter. */
  readonly counterpartyCode: string
  readonly fileId: string
  /** One record out for every record in, INCLUDING the rejected ones — that population is the file's whole point. */
  readonly recordCount: number
  /** The digest the database computed over the rendered bytes, at COMMIT. */
  readonly contentDigest: string
  /**
   * ★ *"Produced is not sent"*, from the server. Identical to
   * {@link SettlementBatchListResponse.outcomeHandoverNote} — see there.
   */
  readonly handoverNote: string
  /** Present only for `ALREADY_GENERATED`: the route's own words on why this is not a second batch. */
  readonly note?: string
  /** The route that reads the artefact back. Not rendered — the screen re-reads its own list instead. */
  readonly readBackVia?: string
}

/** ★★ WAVE 1b. What one release produced — Annex A story 5. */
export interface SettlementEmitResponse {
  readonly outcome: 'GENERATED' | 'NOTHING_TO_EMIT'
  readonly counterpartyCode: string
  /** `null` for `NOTHING_TO_EMIT` — no artefact was committed, deliberately. */
  readonly fileId: string | null
  readonly recordCount: number
  readonly totalPayableCents: number | null
  readonly contentDigest?: string
  /**
   * ★ SECURITY.md S-063. Whether this caller will be able to READ the artefact
   * they just released. Reading needs a grant on the Payment Agency it is
   * addressed to; releasing needs one on the agencies whose BILLS it carries —
   * two different axes that frequently do not line up.
   */
  readonly mayReadItBack?: boolean
  /** Present exactly when `mayReadItBack` is false. The server's own wording. */
  readonly readBackNote?: string
  /**
   * ★★★ WAVE 24 — bills still eligible for this counterparty after the
   * release, present ONLY when the release hit its per-run record bound.
   *
   * Absent on an ordinary complete release, deliberately: a zero on every
   * response is a field readers learn to skip, and then miss on the one release
   * where it matters.
   */
  readonly remainingEligible?: number
  /** The server's own sentence for {@link remainingEligible}. Rendered verbatim. */
  readonly remainingNote?: string
}

export interface SettlementBatchMember {
  readonly billReference: string
  readonly hspAgencyCode: string
  readonly officerId: string
  readonly serviceDate: string
  readonly payableToHspCents: number
  readonly subsidyCents: number
  readonly copayCents: number
  readonly appliedRuleId: string
  readonly appliedRuleVersion: number
  readonly appliedRateBp: number
}

/**
 * What one axis of a paged response was narrowed to, published so the screen never
 * has to guess — the same shape `MatrixAxisPaging` carries on the access-control
 * matrix, and for the same reason: a `PAGE_SIZE` constant on the client would be a
 * second copy of a byte budget the gateway derives, and the copy that drifts is
 * always the one that looks derived.
 */
export interface SettlementMemberPaging {
  /** The page size the SERVER served — the screen's page arithmetic reads this, never a local constant. */
  readonly limit: number
  readonly offset: number
  /** The whole membership under the SAME scope predicate as the rows — never the page. */
  readonly total: number
  /** Rows actually on this page. Fewer than `limit` on the last page. */
  readonly returned: number
}

export interface SettlementBatchMembersResponse {
  readonly members: readonly SettlementBatchMember[]
  /**
   * ⚠ A DICTIONARY the rows are matched against, not a per-row field — the
   * largest demonstration batch carries 1,059 members riding at most twelve
   * distinct rules, and repeating a ~470-character sentence per row would add
   * ~290 KB to a payload the P2C3 §7.1.6 50 KB cap already governs.
   *
   * ⚠★ **WHOLE-ARTEFACT, not the page — changed 2026-08-20 when the members read was
   * paged.** It used to be filtered to the rules the returned members rode, which was
   * the same set while the response WAS the whole batch. Once the members are a page
   * of 50 it is not, and a page-scoped disclosure would let page 4 of a batch whose
   * money rode a fabricated CPW rate render with no note at all. So a note may now
   * appear here with no `[1]` marker on this page's rows; `pagingNote` and the
   * screen's own heading say so. Matching is still on `(ruleId, version)` — see
   * `services/gateway/src/rate-provenance.ts`.
   */
  readonly rateProvenance: readonly RateProvenance[]
  /** ★★ PAGED 2026-08-20 — see `SETTLEMENT_MEMBER_PAGE_SIZE` in the gateway for the byte arithmetic. */
  readonly paging: SettlementMemberPaging
  /** The server's own sentence on what the walk guarantees and what the disclosure covers. */
  readonly pagingNote: string
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 10 (Task 35) — reference-data catalogue and entries, Annex A 40-47.
// Both endpoints already existed (`GET /api/reference`, `GET /api/reference/:parameter`);
// this screen is the first UI over either.
// ─────────────────────────────────────────────────────────────────────────

export interface ReferenceParameterCatalogueEntry {
  readonly parameter: string
  readonly story: string
  readonly label: string
  readonly keyLabel: string
  readonly valueLabel: string
  readonly valueKind: 'CENTS' | 'WHOLE_DAYS' | 'CODE'
  readonly agencyScoped: boolean
  /**
   * ★★ WAVE 3 — P2C11 §14.6(c)/(d)'s bounds, as the SERVER publishes them.
   *
   * ⚠ These have always been on the wire (`GET /api/reference` returns the
   * whole catalogue row); the client type simply did not declare them, so no
   * screen could show an officer what a key or a value has to look like before
   * they submit one. Declaring them is what lets the write form state the rule
   * up front rather than answering with a 400 afterwards — and it is the SAME
   * set of numbers the route validates against and the database's own trigger
   * enforces, never a third copy.
   */
  readonly validation: {
    readonly keyMinLength: number
    readonly keyMaxLength: number
    readonly keySyntax: string
    readonly valueMinLength: number | null
    readonly valueMaxLength: number | null
    readonly valueSyntax: string | null
    readonly valueMin: number | null
    readonly valueMax: number | null
  }
  /**
   * The one-sentence status of this parameter. **Non-null for every one of the
   * ten** — `reference-repository.test.ts` asserts none is null, and this
   * comment claimed the opposite (*"non-null exactly for the parameters that
   * ship in their explicit 'not defined by the pack' state"*) until Wave 9.
   * `string | null` is kept because the column is nullable in the schema.
   */
  readonly referenceModelNote: string | null
  /**
   * ★★ WAVE 9 — whether a running code path RESOLVES this parameter's value,
   * as the server states it.
   *
   * This screen used to render every `referenceModelNote` inside
   * `UnbuiltCard`, whose closing line is a fixed *"Not built yet. No action
   * will make data appear here."* — printed over notes that said the parameter
   * is read at `POST /api/bills/:reference/compute`. Eight of the ten are read
   * today, so the card contradicted its own body text on eight parameters,
   * including every one the money rides. The fix is not for the screen to
   * match words in the sentence — that is a second copy of the rule in the
   * layer furthest from it — but for the server to publish the status. This is
   * it; `058_reference_model_status.sql` carries the argument.
   */
  readonly referenceModelStatus: 'READ' | 'PARTIALLY_READ' | 'NOT_READ'
}

export interface ReferenceParameterListResponse {
  readonly parameters: readonly ReferenceParameterCatalogueEntry[]
}

export interface ReferenceValueEntry {
  readonly key: string
  readonly agencyCode: string | null
  readonly value: { readonly kind: 'CENTS'; readonly cents: number } | { readonly kind: 'WHOLE_DAYS'; readonly days: number } | { readonly kind: 'CODE'; readonly code: string } | null
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly note: string | null
}

/**
 * ★★ WAVE 3 — what `POST /api/reference/:parameter/supersede` answers.
 *
 * ⚠ **Two outcomes, and they are different events.** `SUPERSEDED` opened a new
 * window and closed the one it replaces; `RETIRED` closed the open window with
 * NO successor, so from that date the parameter has no value at all. A screen
 * that reported both as "saved" would hide the second, which is the one that
 * changes how a bill prices.
 */
export type ReferenceSupersedeResponse =
  | {
      readonly outcome: 'SUPERSEDED'
      readonly closedPrevious: boolean
      readonly entry: ReferenceValueEntry
    }
  | {
      readonly outcome: 'RETIRED'
      readonly parameter: string
      readonly key: string
      readonly agencyCode: string | null
      readonly effectiveUntil: string
    }

/**
 * ★★★ WAVE 12 (P5) — Annex A story 40: does the adjustment-indicator register
 * agree with the published file-exchange grammar, on one date of medical service?
 *
 * ⚠ `agreement` is a THREE-member union, never a boolean, and the screen must
 * render all three. SILENT (nobody authored the entry) and DISAGREES (somebody
 * authored a different one) have different remedies, and a UI that showed one
 * tick-or-cross would send half of its readers to the wrong action.
 */
export interface IndicatorVerdict {
  readonly agreement: 'AGREES' | 'SILENT' | 'DISAGREES'
  readonly indicator: string
  /** Present on AGREES: what both sides say. */
  readonly billType?: string
  /** Present on DISAGREES: what the register says. */
  readonly registerBillType?: string
  /** Present on SILENT and DISAGREES: what the grammar says. */
  readonly declaredBillType?: string
  readonly effective?: { readonly from: string; readonly until: string | null }
  readonly recordedAt?: string
}

export interface IndicatorReconciliationResponse {
  readonly parameter: string
  readonly resolvedServiceDate: string
  readonly verdicts: readonly IndicatorVerdict[]
  /** ★ PUBLISHED by the route, never recomputed here — P2C11 §14.6's discipline. */
  readonly reconciled: boolean
  /** What a non-AGREES verdict MEANS for the next delivery, in the server's words. */
  readonly consequence: string
}

export interface ReferenceEntriesResponse {
  readonly parameter: string
  readonly resolvedServiceDate: string
  readonly asRecordedAt: string | null
  readonly entries: readonly ReferenceValueEntry[]
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 10 (Annex A story 51) — Financial Counselling cost estimation.
// Every response is labelled an ESTIMATE — see routes/cost-estimate.ts's own
// header for why nothing here is, or resembles, a computed bill.
// ─────────────────────────────────────────────────────────────────────────

export interface CostEstimateResponse {
  readonly kind: 'ESTIMATE'
  readonly disclaimer: string
  readonly officerId: string
  readonly serviceDate: string
  readonly scheme: string | null
  readonly wardClass: string | null
  readonly billAmountCents: number
  readonly subsidyCents: number
  readonly coPaymentCents: number
  readonly appliedRule: {
    readonly id: string
    readonly version: number
    readonly rate: number
    readonly serviceDate: string
    readonly recordedAt: string
  }
  /**
   * ⚠ The estimate is the surface where an invented rate does the most damage: a
   * bill detail describes money that has already moved, this describes money
   * somebody is deciding about. Resolved by the server against the very rule set
   * that produced the figure above.
   */
  readonly rateProvenance: RateProvenance | null
}

/** The 400 `POST /api/cost-estimate` answers when a ward-based scheme has no `wardClass` yet. */
export interface CostEstimateWardClassRequired {
  readonly reason: 'WARD_CLASS_REQUIRED'
  readonly scheme: string
  readonly message: string
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 10 (Annex A stories 24, 25) — online help and the user manual.
// ─────────────────────────────────────────────────────────────────────────

export interface HelpTopicSummary {
  readonly id: string
  readonly category: string
  readonly title: string
  readonly summary: string
}

export interface HelpTopicsResponse {
  readonly manualVersion: string
  readonly manualUpdatedOn: string
  readonly topics: readonly HelpTopicSummary[]
}

export interface HelpTopicResponse extends HelpTopicSummary {
  readonly body: readonly string[]
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 10 (Annex A story 49) — print CS card information.
// ─────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 10 (Annex A story 50) — the notification RECORD and its trigger.
// Nothing here is ever delivered — see routes/notifications.ts's own header.
// ─────────────────────────────────────────────────────────────────────────

export interface NotificationRule {
  readonly id: string
  readonly eventType: string
  readonly recipientList: string
  readonly templateCode: string | null
  readonly enabled: boolean
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly note: string | null
}

export interface NotificationRulesResponse {
  readonly rules: readonly NotificationRule[]
  /** ★★ WAVE 8 (Task 3) — the bounds this catalogue's route enforces, published as data. */
  readonly validation: PublishedBounds<'recipientList'>
  readonly eventTypes: readonly string[]
}

export interface NotificationLogEntry {
  readonly id: string
  readonly eventType: string
  readonly resourceType: string
  readonly resourceId: string | null
  readonly ruleId: string | null
  readonly recipientsSnapshot: string | null
  readonly templateCodeSnapshot: string | null
  /**
   * ⚠ WIDENED BY TASK 3 OF THE 2026-08-15 NOTIFICATION-DELIVERY WAVE, in the
   * same change that made the server able to produce the second value.
   * `recordNotificationEvent` now writes `'QUEUED'` when a rule, a template
   * and a recipient list all resolve (see `notification-repository.ts`), and
   * the delivery OUTCOME then lives on `app.notification_delivery`, never
   * here. This type is a SEPARATE declaration from the server's, so nothing
   * would have failed to compile had it been left reading `'NOT_SENT'` — it
   * would simply have been wrong, and the first `switch` written against it
   * would have inherited that. The SCREEN that renders the queue behind a
   * QUEUED row is Task 7's, not this line's.
   */
  readonly status: 'NOT_SENT' | 'QUEUED'
  readonly triggeredAt: string
  readonly note: string
}

export interface NotificationLogResponse {
  readonly entries: readonly NotificationLogEntry[]
  readonly total: number
  readonly notice: string
}

export interface NotificationTriggerResponse {
  readonly entry: NotificationLogEntry
  readonly notice: string
}

// ─────────────────────────────────────────────────────────────────────────
// ★ TASK 7 (2026-08-15 notification-delivery wave) — the operator's own read of
// the outbox Task 5 built: `GET /api/notifications/deliveries`,
// `.../:id/attempts`, `POST .../outbox/run`, `POST .../:id/requeue`. Field
// names mirror `NotificationDeliveryRow`/`NotificationDeliveryAttemptRow` in
// `services/administration/src/notification-delivery-repository.ts` exactly —
// `apps/officer-web` may not import that package (it is a `context`, this app
// is `app`; `packages/architecture/src/contexts.ts` allows `app -> kernel`
// only), so these are copies of the WIRE SHAPE, not the server's own types.
// ─────────────────────────────────────────────────────────────────────────

/**
 * ⚠ `status` is a bare `string`, deliberately NOT a client-declared union —
 * the same lesson `AuditEvent.outcome` records above: a client enum is a claim
 * about the server, and this app has no `isNotificationDeliveryStatus` to
 * validate against (that guard lives in `@prs2/administration`, a context this
 * layer may not import). The six values the server's own CHECK admits today
 * are QUEUED/SENDING/SENT/SIMULATED/BOUNCED/ABANDONED, and `?status=` filter
 * options are read from THIS response's own `statuses` field
 * (`NotificationDeliveryListResponse`) rather than restated here — the
 * gateway route's own comment is explicit that "the recognised set rides on
 * the response so a client never has to guess it."
 *
 * ⚠ NO `body` — P2C3 §7.1.6's 50 KB APEX cap; see that route's own header.
 * `recipient`/`subject`/`lastError`/`providerMessageId` are visibly truncated
 * server-side with a trailing "…", never silently.
 */
export interface NotificationDeliveryRow {
  readonly id: string
  readonly notificationLogId: string
  /**
   * ★ WHICH EVENT this delivery belongs to, projected from its
   * `app.notification_log` parent — see the server's own
   * `NotificationDeliveryRow.eventType` for why it was missing and what the
   * screen was claiming in its absence.
   *
   * ⚠ A bare `string`, like `status` above and for the same reason: the set of
   * event types is the SERVER's, it grew from one to four in a single task, and
   * a client-declared union would be a claim about it.
   */
  readonly eventType: string
  readonly recipient: string
  readonly subject: string
  readonly status: string
  /** The CURRENT retry budget consumed — resets to 0 on a requeue. NOT the same number as `attemptCount`. */
  readonly attempts: number
  /** Rows in this delivery's append-only attempt ledger. Never resets, even across a requeue. */
  readonly attemptCount: number
  readonly nextAttemptAt: string
  readonly claimedAt: string | null
  readonly lastError: string | null
  readonly providerMessageId: string | null
  readonly transportId: string | null
  readonly queuedAt: string
  readonly terminalAt: string | null
}

/**
 * `GET /api/notifications/deliveries` — paginated (`clampLimit`-bounded, no
 * cursor: the whole outbox is capped small enough that one page is the
 * contract), sortable through the server's own allow-list, filterable by
 * `?status=`.
 *
 * ⚠ `notificationDelivery` is the RAW value this response carried — read it
 * through `readNotificationDelivery`/`isNotificationDelivery` from
 * `@prs2/contracts` at the call site, never trusted as the bare union
 * directly. It is what decides whether Run outbox/Requeue are safe to leave
 * enabled, which is exactly the class of field `requestSelfReporting` narrows
 * for `/api/me`/`/api/identities`/`POST /api/session` — this route carries the
 * SAME field over a plain `request<T>` cast instead, so the narrowing has to
 * happen where it is READ, not where it is fetched.
 */
export interface NotificationDeliveryListResponse {
  readonly deliveries: readonly NotificationDeliveryRow[]
  /** Count under the SAME `?status=` filter, never the whole table. */
  readonly total: number
  /** Every status `?status=` will accept today — read this rather than a client-side list. */
  readonly statuses: readonly string[]
  /** Every column `?sort=` will accept today. */
  readonly sortColumns: readonly string[]
  readonly notificationDelivery: string
  readonly notice: string
}

/** One row of `app.notification_delivery_attempt` — the append-only ledger for ONE delivery, oldest first, never sortable. */
export interface NotificationDeliveryAttemptRow {
  readonly attemptNo: number
  readonly transportId: string
  readonly outcome: string
  readonly smtpCode: number | null
  readonly detail: string
  readonly startedAt: string
  readonly finishedAt: string
}

/**
 * `GET /api/notifications/deliveries/:id/attempts` — 404 for an unknown id,
 * distinguished from a 200 with `attempts: []` (queued but never attempted).
 */
export interface NotificationDeliveryAttemptsResponse {
  readonly delivery: NotificationDeliveryRow
  readonly attempts: readonly NotificationDeliveryAttemptRow[]
  /** Same RAW-value discipline as `NotificationDeliveryListResponse.notificationDelivery` — see there. */
  readonly notificationDelivery: string
  readonly notice: string
}

/** The six outcome counters `runNotificationOutboxOnce` reports — they sum to exactly `claimed`. */
export interface NotificationOutboxRunResult {
  readonly claimed: number
  readonly sent: number
  readonly simulated: number
  readonly bounced: number
  readonly retrying: number
  readonly abandoned: number
  readonly notConfigured: number
}

/** `POST /api/notifications/outbox/run` — 200. A 409 (`CLAIM_LOST`) surfaces as an `ApiError`; see `api.ts`'s own header note on that response. */
export interface NotificationOutboxRunResponse {
  readonly outcome: 'RAN'
  readonly result: NotificationOutboxRunResult
  readonly transportId: string
  /** Same RAW-value discipline as `NotificationDeliveryListResponse.notificationDelivery` — see there. */
  readonly notificationDelivery: string
  readonly notice: string
}

/** `POST /api/notifications/deliveries/:id/requeue` — 200, for an ABANDONED row only. Every refusal (404/409) surfaces as an `ApiError` carrying the server's own message, including the way out. */
export interface NotificationRequeueResponse {
  readonly outcome: 'REQUEUED'
  readonly delivery: NotificationDeliveryRow
  /** Same RAW-value discipline as `NotificationDeliveryListResponse.notificationDelivery` — see there. */
  readonly notificationDelivery: string
  readonly notice: string
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 11 (Annex A story 39) — the scoped FAQ. No external LLM ever
// touches a question — see routes/faq.ts's own header.
// ─────────────────────────────────────────────────────────────────────────

export interface FaqListEntry {
  readonly id: string
  readonly question: string
  readonly answer: string
  readonly relatedTopicId: string
}

export interface FaqListResponse {
  readonly entries: readonly FaqListEntry[]
}

export interface FaqAskResponse {
  readonly matched: boolean
  readonly answers?: readonly { readonly id: string; readonly question: string; readonly answer: string; readonly relatedTopicId: string }[]
  readonly message?: string
  readonly suggestions?: readonly { readonly id: string; readonly question: string }[]
}

// ─────────────────────────────────────────────────────────────────────────
// ★ PHASE 11 (Annex A story 48) — local support tickets. externalTicketRef
// is ALWAYS null — no live WOG ITSM connection exists.
// ─────────────────────────────────────────────────────────────────────────

export interface SupportTicket {
  readonly id: string
  readonly subject: string
  readonly description: string
  readonly priority: string
  readonly systemComponent: string | null
  readonly raisedBy: string
  readonly status: string
  readonly externalTicketRef: string | null
  readonly createdAt: string
  readonly updatedAt: string
}

export interface SupportTicketEvent {
  readonly id: string
  readonly fromStatus: string | null
  readonly toStatus: string
  readonly actor: string
  readonly occurredAt: string
  readonly note: string | null
}

export interface CreateSupportTicketResponse {
  readonly ticket: SupportTicket
  readonly notice: string
}

export interface SupportTicketListResponse {
  readonly tickets: readonly SupportTicket[]
  readonly total: number
  /** ★★ WAVE 8 (Task 3) — the bounds this catalogue's route enforces, published as data. */
  readonly validation: PublishedBounds<'subject' | 'description' | 'systemComponent'>
}

export interface SupportTicketDetailResponse {
  readonly ticket: SupportTicket
  readonly events: readonly SupportTicketEvent[]
}

export interface CsCardResponse {
  readonly officerId: string
  readonly displayName: string
  readonly agencyCode: string
  readonly agencyName: string
  readonly scheme: string | null
  readonly wardClass: string | null
  readonly entitlementFrom: string | null
  readonly entitlementUntil: string | null
  readonly asOfDate: string
  readonly printedAt: string
}

/**
 * ★★ WAVE 2b — Annex A story 13, the custom report builder's own catalogue.
 *
 * ⚠ **Every member of this is SERVER-SUPPLIED and none of it is restated here.**
 * The field list a picker offers comes from `GET /api/reports/custom/fields`,
 * never from a constant in this app: `apps/officer-web` cannot import from
 * `services/gateway`, so a hand-copied list would drift the moment a field was
 * added — and it would drift SILENTLY, because the server would go on refusing
 * an id the client had stopped sending and nothing would ever go red.
 */
export interface CustomReportField {
  readonly id: string
  readonly label: string
  readonly type: string
}

export interface CustomReportFieldsResponse {
  readonly fields: readonly CustomReportField[]
  /** The row bound the EXPORT refuses above — never truncates. */
  readonly maxRows: number
  readonly previewPageSize: number
  readonly maxPreviewPageSize: number
  readonly formats: readonly string[]
  readonly billTypes: readonly string[]
  readonly statuses: readonly string[]
}

export interface CustomReportPreviewResponse {
  readonly columns: readonly CustomReportField[]
  /** Already formatted by the SERVER, with the same formatter the document uses. */
  readonly rows: readonly Readonly<Record<string, string>>[]
  readonly total: number
  readonly limit: number
  readonly offset: number
  /** True when this filter selects more rows than a document may carry, so the export would refuse. */
  readonly exceedsExportBound: boolean
  readonly maxRows: number
  /**
   * The provenance of the rates behind THIS PAGE's rows — present even when the
   * officer did not select the `Applied Rule` column, because the server always
   * resolves it from `app.bill`'s own provenance columns rather than from the
   * chosen field set.
   */
  readonly rateProvenance: readonly RateProvenance[]
}

// ─────────────────────────────────────────────────────────────────────────
// ★★ ADR 0011 — the PSD account directory, READ-ONLY. Annex A stories 52-59.
//
// ⚠ Every caveat is a SERVER-SUPPLIED string, not copy written on the screen.
// What PSD may do to an account is a governance decision recorded in ADR 0011,
// and a screen that restated it could drift from what the routes permit — which
// on this surface means telling a PSD officer they cannot do something they can,
// or worse.
// ─────────────────────────────────────────────────────────────────────────

export interface DirectoryAccount {
  readonly id: string
  readonly email: string
  readonly displayName: string
  readonly role: string
  readonly agencyCode: string
  readonly isActive: boolean
}

export interface AccountDirectoryResponse {
  readonly accounts: readonly DirectoryAccount[]
  readonly total: number
  readonly nextCursor: string | null
  /** Why there is no write control here, and who does write. */
  readonly writeAuthorityNote: string
  /** That the CAM integration is a shared header credential today (Bucket A/B). */
  readonly integrationNote: string
}

export interface DirectoryGroup {
  readonly id: string
  readonly name: string
  readonly description: string | null
  readonly memberUserIds: readonly string[]
}

export interface AccountGroupsResponse {
  readonly groups: readonly DirectoryGroup[]
  readonly total: number
  readonly nextCursor: string | null
  /** That a group confers NO app.user_scope grant — A-46 / Q-35, unanswered. */
  readonly membershipNote: string
  readonly writeAuthorityNote: string
}


// ══════════════════════════════════════════════════════════════════════════════
// ★★ ANNEX A STORY 17 — the access-control matrix, all three axes in one read
// ══════════════════════════════════════════════════════════════════════════════

/**
 * ⚠ `heldBy` lists only the roles that HOLD the capability; absence is the answer
 * for the rest. The gateway publishes it that way because the explicit
 * nine-entry-per-capability form cost 90% of the 50 KB payload budget on an
 * eleven-account estate — see `routes/access-control-matrix.ts`'s own header.
 */
export interface MatrixCapability {
  readonly name: string
  readonly heldBy: readonly string[]
}

export interface MatrixGrant {
  readonly agencyCode: string
  readonly billType: string
  readonly approvalLevel: number
  /** `null` means NO monetary ceiling — never a sentinel, and never rendered blank. */
  readonly approvalLimit: number | null
}

export interface MatrixAccount {
  readonly userId: string
  readonly email: string
  readonly displayName: string
  readonly role: string
  /** The agency the ACCOUNT belongs to, which is not necessarily any grant's agency. */
  readonly agencyCode: string
  readonly agencyKind: string
  readonly isActive: boolean
  readonly grants: readonly MatrixGrant[]
}

export interface MatrixFinding {
  readonly kind: string
  readonly severity: 'ATTENTION' | 'REVIEW'
  readonly userId: string
  readonly email: string
  readonly role: string
  readonly statement: string
  readonly evidence: string
  readonly tracesTo: string
}

/**
 * ★★ What each detector examined and what it found.
 *
 * ⚠ RENDERED, not dropped when `found` is 0. Three of the five detectors find
 * nothing on a healthy estate, and a screen that shows only findings is
 * indistinguishable from one whose detectors never ran.
 */
export interface MatrixDetectorTally {
  readonly kind: string
  readonly examined: number
  readonly unit: string
  readonly found: number
  /**
   * ★★ WAVE 9 — why this detector did NOT run, when it did not.
   *
   * ⚠ `undefined` means it ran, so `found: 0` with no reason is the positive
   * statement *"examined N, found nothing"*. A detector that could not run reports
   * `examined: 0` AND this sentence, because those two together cannot be misread
   * as a clean bill and `examined: 0, found: 0` on its own can — which is the same
   * argument the tallies themselves rest on, applied one level down.
   *
   * `GRANT_MATCHES_NO_DATA` is the one detector that can be in this state: it needs
   * an estate-activity measurement, and it refuses an all-zero one because a fresh
   * installation and a broken probe produce the identical input.
   */
  readonly unavailable?: string
}

export interface MatrixAnalysis {
  /**
   * ★★ WHOLE-ESTATE, not this page — and the distinction is the point.
   *
   * ⚠ Three of the five detectors find nothing on a healthy estate. If these
   * counts and the tallies below described only the twenty accounts on the current
   * page, a reviewer reading "GRANT_IS_REDUNDANT: found 0" off page one of
   * seventeen would file the estate as clean. The gateway computes them over the
   * whole (filtered) estate on every page and says so in `notes`, which this screen
   * renders verbatim.
   */
  readonly accountsExamined: number
  readonly grantsExamined: number
  readonly detectors: readonly MatrixDetectorTally[]
  /** ⚠ THIS PAGE of the findings axis. `paging.findings.total` is how many there are. */
  readonly findings: readonly MatrixFinding[]
}

/** One paged axis, exactly as the gateway published it — never recomputed here. */
export interface MatrixAxisPaging {
  readonly limit: number
  readonly offset: number
  /** The whole (filtered) estate under the same predicate as the rows. */
  readonly total: number
  readonly returned: number
  readonly sort: string | null
  readonly dir: 'asc' | 'desc'
}

/**
 * ★★ TWO pagers, because the two axes that grow with the estate grow at different
 * rates — the findings axis is 69% of the body at ~707 bytes a row and the user
 * axis is ~471. One shared offset would mean "page 3" of a 300-row axis and an
 * 1,100-row axis at the same time, which is two different pages.
 */
export interface MatrixPaging {
  readonly accounts: MatrixAxisPaging
  readonly findings: MatrixAxisPaging
}

export interface AccessControlMatrixResponse {
  readonly generatedAt: string
  readonly roles: readonly { role: string; label: string }[]
  /** ⚠ NOT paged: enumerated from packages/auth, the same size whatever the estate does. */
  readonly capabilities: readonly MatrixCapability[]
  /** ⚠ THIS PAGE of the user axis. `paging.accounts.total` is the estate. */
  readonly accounts: readonly MatrixAccount[]
  readonly analysis: MatrixAnalysis
  readonly paging: MatrixPaging
  readonly notes: readonly string[]
  /**
   * The filter axes Annex A 17 names that this system cannot honour, each with its
   * reason. Rendered on screen so an officer reads WHY rather than discovering a
   * 400 by trying it.
   */
  readonly filtersNotAvailable: Record<string, string>
}

// ─────────────────────────────────────────────────────────────────────────
// ★★★ WAVE 12 (P2) — Annex A story 14 criteria 1 and 2: the identity providers
// this deployment offers, and how a verified claim resolves to a local account.
// ─────────────────────────────────────────────────────────────────────────

export interface IdentityProvider {
  readonly id: string
  readonly providerKey: string
  readonly displayName: string
  readonly enabled: boolean
  readonly issuer: string | null
  readonly clientId: string | null
  readonly authorizationEndpoint: string | null
  readonly tokenEndpoint: string | null
  readonly jwksUri: string | null
  readonly requiredAssuranceLevel: string | null
  readonly displayOrder: number
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly note: string | null
  /**
   * ★★★ Whether this deployment could actually honour the door, as the SERVER
   * computes it — never derived here.
   *
   * ⚠ THREE states, not two: `enabled: false` is "not offered"; `enabled: true,
   * installable: true` is a working door; `enabled: true, installable: false` is a
   * door the Authority has turned on that this deployment has no credential for.
   * Collapsing the last two is how a screen comes to offer a sign-in that cannot
   * complete — and it is exactly the state the seeded WOG_ADFS / SINGPASS /
   * CORPPASS rows are in, because no client registration exists (Bucket B).
   */
  readonly installable: boolean
}

export interface IdentityMappingRule {
  readonly id: string
  readonly providerKey: string
  readonly claimName: string
  readonly localField: string
  readonly normaliseCase: boolean
  readonly effective: { readonly from: string; readonly until: string | null }
  readonly recordedAt: string
  readonly note: string | null
}

export interface IdentityProviderListResponse {
  readonly providers: readonly IdentityProvider[]
  readonly mappingRules: readonly IdentityMappingRule[]
  /** ★★ The bounds this catalogue's route enforces, published as data (P2C11 §14.6). */
  readonly validation?: PublishedBounds<'displayName' | 'note'>
  /** What the gateway ACTUALLY installed, so the catalogue and the running deployment can be read side by side. */
  readonly installedProvider: string
  readonly notes: readonly string[]
}

// ─────────────────────────────────────────────────────────────────────────────
// ★★ ANNEX A 22 — the MSO pro-ration rule engine. Wire shapes of
// `services/gateway/src/routes/mso-rules.ts`, mirrored here because this bundle
// may not import the server-side rules package (it reads `node:crypto`).
// `lib/mso-vocabulary.test.ts` proves the mirrored vocabulary has not drifted.
// ─────────────────────────────────────────────────────────────────────────────

export type MsoRuleStatus = 'DRAFT' | 'ACTIVE' | 'SUPERSEDED' | 'RETIRED'
export type MsoScalar = string | boolean

export interface MsoCondition {
  readonly field: string
  readonly op: string
  readonly value?: MsoScalar | readonly MsoScalar[]
}

export interface MsoComponentShare {
  readonly component: string
  readonly officerBasisPoints: number
}

export interface MsoDecisionRow {
  readonly id: string
  readonly label: string
  readonly when: readonly MsoCondition[]
  readonly shares: readonly MsoComponentShare[]
}

export interface MsoRuleDefinition {
  readonly schemaVersion: 1
  readonly applicability: readonly (readonly MsoCondition[])[]
  readonly precedence: number
  readonly requiredInputs: readonly string[]
  readonly table: readonly MsoDecisionRow[]
  readonly rounding: { readonly mode: string; readonly scope: 'PER_COMPONENT'; readonly remainderTo: 'GOVERNMENT' }
}

export interface MsoRuleVersion {
  readonly id: string
  readonly ruleKey: string
  readonly version: number
  readonly status: MsoRuleStatus
  readonly effectiveFrom: string
  readonly effectiveUntil: string | null
  readonly definition: MsoRuleDefinition
  readonly definitionHash: string
  readonly createdBy: string | null
  readonly lastEditedBy: string | null
  readonly createdAt: string
  readonly activatedBy: string | null
  readonly activatedAt: string | null
  readonly provenanceNote: string | null
  /** Display names for the three ids above. ⚠ Show these, never the raw id; 'Not recorded' only when both are null. */
  readonly createdByName?: string | null
  readonly lastEditedByName?: string | null
  readonly activatedByName?: string | null
}

export interface MsoRuleSummary {
  readonly ruleKey: string
  readonly name: string
  readonly purpose: string
  readonly active: MsoRuleVersion | null
  readonly draft: MsoRuleVersion | null
}

export interface MsoTestCase {
  readonly ruleKey: string
  readonly caseId: string
  readonly source: 'AUTHORITY_PACK' | 'AUTHOR'
  readonly description: string
  readonly input: Record<string, unknown>
  readonly expectedOfficerCents: number | null
  readonly expectedGovernmentCents: number | null
  readonly expectedRefusalKind: string | null
}

export interface MsoTraceStep {
  readonly step: number
  readonly code: string
  readonly summary: string
  readonly detail: Readonly<Record<string, unknown>>
}

export interface MsoCaseResult {
  readonly caseId: string
  readonly source: string
  readonly passed: boolean
  readonly expected: { officerCents: number | null; governmentCents: number | null; refusalKind: string | null }
  readonly actual: {
    officerCents: number | null
    governmentCents: number | null
    refusalKind: string | null
    ruleKey: string | null
  }
  readonly trace: readonly MsoTraceStep[] | unknown
}

export interface MsoTestRun {
  readonly id: string
  readonly ruleVersionId: string
  readonly definitionHash: string
  readonly runBy: string | null
  readonly runAt: string
  readonly caseCount: number
  readonly passed: boolean
  readonly results: unknown
}

export interface MsoRefusal {
  readonly kind: string
  readonly message: string
  readonly fields: readonly string[]
}

export interface MsoComponentResult {
  readonly component: string
  readonly amountCents: number
  readonly officerBasisPoints: number
  readonly exactOfficerCents: string
  readonly officerCents: number
  readonly governmentCents: number
  readonly roundingAdjustment: string
}

export interface MsoEvaluation {
  readonly outcome: 'CALCULATED' | 'REFUSED'
  readonly refusal: MsoRefusal | null
  readonly ruleKey: string | null
  readonly ruleVersionId: string | null
  readonly ruleVersion: number | null
  readonly rowId: string | null
  readonly officerCents: number | null
  readonly governmentCents: number | null
  readonly components: readonly MsoComponentResult[]
  readonly trace: readonly MsoTraceStep[]
}

export interface MsoCalculation {
  readonly id: string
  readonly computedAt: string
  readonly computedBy: string | null
  /** The display name of `computedBy`; null when the officer cannot be resolved. */
  readonly computedByName?: string | null
  readonly input: unknown
  readonly outcome: 'CALCULATED' | 'REFUSED'
  readonly refusalKind: string | null
  readonly ruleVersionId: string | null
  readonly ruleKey: string | null
  readonly ruleVersion: number | null
  readonly officerCents: number | null
  readonly governmentCents: number | null
  readonly trace: unknown
}

// ── Q-54: the read-only comparison of the MSO engine with the flat-rate path ─────────────────────────

export type MsoBasisSource = 'DEMONSTRATION' | 'OFFICER_ENTRY' | 'HSP_FILE'

export interface MsoBasis {
  readonly billReference: string
  readonly beneficiaryRef: string | null
  readonly beneficiaryType: 'PRINCIPAL' | 'DEPENDANT'
  readonly careSetting: 'OUTPATIENT' | 'INPATIENT'
  readonly visitType: 'FIRST_CONSULTATION' | 'FOLLOW_UP' | null
  readonly hspType: 'SPECIALIST' | 'POLYCLINIC' | 'HOSPITAL' | null
  readonly governmentReferral: boolean | null
  readonly billIndicator: 'NORMAL' | 'MEDISAVE'
  readonly specialBillCondition: 'DETERMINATION_OF_FITNESS' | 'SERVICE_INJURY' | 'NONE'
  readonly eligibleWardClass: string | null
  readonly billedWardClass: string | null
  readonly voluntaryWardUpgrade: boolean | null
  readonly source: MsoBasisSource
  readonly recordedBy: string | null
  readonly recordedByName: string | null
  readonly recordedAt: string
}

export interface MsoFlatSplit {
  readonly computed: boolean
  readonly governmentCents: number | null
  readonly officerCents: number | null
  readonly ruleId: string | null
  readonly ruleVersion: number | null
  readonly rateBasisPoints: number | null
}

export interface MsoEngineSplit {
  readonly outcome: 'CALCULATED' | 'REFUSED'
  readonly refusalKind: string | null
  readonly refusalMessage: string | null
  readonly ruleKey: string | null
  readonly ruleVersion: number | null
  readonly rowId: string | null
  readonly governmentCents: number | null
  readonly officerCents: number | null
  readonly capStatusSource: string | null
}

export interface MsoShadowComparison {
  readonly billReference: string
  readonly serviceDate: string
  readonly amountCents: number
  readonly basis: MsoBasis
  readonly flat: MsoFlatSplit
  readonly engine: MsoEngineSplit
  /** Engine Government minus flat Government. Positive: Government would pay more. */
  readonly governmentChangeCents: number | null
  readonly componentAssumption: string | null
}

export interface MsoShadowSummary {
  readonly billsCompared: number
  readonly engineCalculated: number
  readonly engineRefused: number
  readonly notYetComputed: number
  readonly flatGovernmentCents: number
  readonly engineGovernmentCents: number
  readonly governmentChangeCents: number
  readonly billsWhoseSplitChanges: number
  readonly byRule: readonly { readonly ruleKey: string; readonly bills: number; readonly governmentChangeCents: number }[]
  readonly byRefusal: readonly { readonly kind: string; readonly bills: number }[]
  readonly truncated: boolean
  readonly ceiling: number
}

export interface MsoComparisonResponse {
  readonly summary: MsoShadowSummary
  readonly total: number
  readonly comparisons: readonly MsoShadowComparison[]
}

export interface MsoBillComparisonResponse {
  readonly comparison: MsoShadowComparison
}

export interface MsoBasisInput {
  readonly beneficiaryRef?: string
  readonly beneficiaryType: string
  readonly careSetting: string
  readonly visitType?: string
  readonly hspType?: string
  readonly governmentReferral?: boolean
  readonly billIndicator?: string
  readonly specialBillCondition?: string
  readonly eligibleWardClass?: string
  readonly billedWardClass?: string
  readonly voluntaryWardUpgrade?: boolean
}

export interface MsoRecordBasisResponse {
  readonly basis: MsoBasis
  readonly comparison: MsoShadowComparison | null
}

export interface MsoCalculateResponse {
  readonly calculation: MsoCalculation
  readonly evaluation: MsoEvaluation
}

/** The server's own length bounds for the free-text MSO fields, published on `GET /api/mso-rules`. */
export interface MsoFieldBounds {
  readonly ruleName: number
  readonly purpose: number
  readonly note: number
  readonly description: number
  readonly rowId: number
  readonly rowLabel: number
  readonly echoedName: number
}

export interface MsoRuleListResponse {
  readonly rules: readonly MsoRuleSummary[]
  readonly bounds?: MsoFieldBounds
}

export interface MsoRuleVersionsResponse {
  readonly ruleKey: string
  readonly versions: readonly MsoRuleVersion[]
  readonly testCases: readonly MsoTestCase[]
}

/** One thing worth checking before a draft is activated. Advice only: it never blocks the Activate button. */
export interface MsoActivationWarning {
  readonly kind: string
  readonly message: string
  readonly ruleKey?: string
  readonly version?: number
}

export interface MsoRuleVersionResponse {
  readonly version: MsoRuleVersion
  readonly testCases: readonly MsoTestCase[]
  readonly testRuns: readonly MsoTestRun[]
  /** Empty unless the version is a DRAFT. Absent on an older server: treated as empty. */
  readonly activationWarnings?: readonly MsoActivationWarning[]
  /** Who is blocked from activating a draft, in the server's words. Draft only; absent on an older server. */
  readonly makerCheckerNote?: string | null
  /** Whether the officer asking may activate this draft. Draft only; absent on an older server. */
  readonly youCanActivate?: boolean | null
}

/**
 * A stored input field the server withheld instead of keeping: a refused calculation does not keep free text
 * that failed validation (a name, an identity number, a 5,000-character string).
 */
export interface RedactedField {
  readonly redacted: true
  readonly reason: string
  readonly length: number
}

export interface MsoTestRunResponse {
  readonly run: MsoTestRun
  readonly results: readonly MsoCaseResult[]
}

export interface MsoCalculationListResponse {
  readonly calculations: readonly MsoCalculation[]
  readonly total: number
}

export interface MsoCalculationDetailResponse {
  readonly calculation: MsoCalculation
  readonly version: MsoRuleVersion | null
}


// ─────────────────────────────────────────────────────────────────────────
// User account administration, `GET/POST/PUT /api/administration/users*`. System Administrator only.
// ─────────────────────────────────────────────────────────────────────────

export const SCOPE_BILL_TYPES = ['NEW', 'UPWARD_ADJUSTMENT', 'DOWNWARD_ADJUSTMENT', 'ALL'] as const
export type ScopeBillTypeValue = (typeof SCOPE_BILL_TYPES)[number]

/** One grant: what an account may see and approve at one agency. `approvalLimitCents` null means no ceiling, which is not zero. */
export interface UserScopeRow {
  readonly agencyCode: string
  readonly billType: ScopeBillTypeValue
  readonly approvalLevel: 0 | 1 | 2
  readonly approvalLimitCents: number | null
}

export interface UserAccount {
  readonly id: string
  readonly email: string
  readonly displayName: string
  readonly role: string
  readonly roleLabel: string
  readonly agencyCode: string
  readonly isActive: boolean
  /** Who switched it off: CAM, the System Administrator, or null while active. */
  readonly disabledBy: 'CAM' | 'SYSTEM_ADMIN' | null
  readonly createdAt: string
  readonly updatedAt: string
  readonly scopeCount: number
}

export interface UserAccountDetail extends UserAccount {
  readonly scopes: readonly UserScopeRow[]
}

export interface AccountChange {
  readonly id: string
  readonly occurredAt: string
  readonly actorEmail: string
  readonly action: 'CREATED' | 'UPDATED' | 'DEACTIVATED' | 'REACTIVATED' | 'PASSWORD_SET'
  readonly before: unknown
  readonly after: unknown
  readonly note: string | null
}

export type UserAccountBounds = PublishedBounds<'email' | 'displayName' | 'note' | 'reason'>

export interface UserAccountListResponse {
  readonly users: readonly UserAccount[]
  readonly total: number
  readonly limit: number
  readonly offset: number
  readonly validation: UserAccountBounds
}

export interface UserRoleTemplate {
  readonly role: string
  readonly label: string
  readonly defaultAgency: string
  readonly defaultScope: readonly UserScopeRow[]
}

export interface UserRolesResponse {
  readonly agencies: readonly string[]
  readonly billTypes: readonly ScopeBillTypeValue[]
  readonly roles: readonly UserRoleTemplate[]
  readonly validation: UserAccountBounds
  readonly note: string
}

export interface UserAccountBody {
  readonly email?: string
  readonly displayName: string
  readonly role: string
  readonly agencyCode: string
  readonly scopes: readonly UserScopeRow[]
  readonly note?: string
}

// ─────────────────────────────────────────────────────────────────────────
// Acting as another account after SSO (ADR 0015), `GET /api/assume/accounts`.
// ─────────────────────────────────────────────────────────────────────────

/** One demonstration account an administrator may act as: active, human, not an administrator, never signed in through SSO. */
export interface AssumableAccount {
  readonly userId: string
  readonly email: string
  readonly displayName: string
  readonly role: string
  readonly roleLabel: string
  /** `Name, Role, Agency`, composed server-side and shown verbatim. */
  readonly label: string
}

export interface AssumeAccountsResponse {
  readonly accounts: readonly AssumableAccount[]
  /** True when more accounts exist than the page offers. */
  readonly truncated: boolean
  /** The administrator's own entry ("Manage users as administrator"). */
  readonly administrator: { readonly email: string; readonly displayName: string }
}
